import * as reactQuery from '@tanstack/react-query';
import { useQueryClient, useMutation, useInfiniteQuery } from '@tanstack/react-query';
import { stringify } from 'qs';
import { requestIdHeaderName, getDateFromString, getStringFromDate } from '@sit/core';
import axios, { AxiosError } from 'axios';
import mem from 'mem';
import { v4 } from 'uuid';
import immer, { produce } from 'immer';
import { useCallback, useMemo } from 'react';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}

/**
 * This token will be overwritten at run-time by each client.
 */
let getToken = () => undefined;
let getRefreshToken = () => undefined;
let setToken = () => undefined;
let onLogout = () => undefined;
let getHeaders = () => ({});
function initializeAuthCallbacks(cbs) {
  // This function is overwritten by each client.
  if (cbs.getToken) getToken = cbs.getToken;
  if (cbs.getRefreshToken) getRefreshToken = cbs.getRefreshToken;
  if (cbs.setToken) setToken = cbs.setToken;
  if (cbs.onLogout) onLogout = cbs.onLogout;
  if (cbs.getHeaders) getHeaders = cbs.getHeaders;
}

const _excluded$4 = ["adapter", "auth", "baseURL", "beforeRedirect", "cancelToken", "data", "decompress", "env", "headers", "httpAgent", "httpsAgent", "insecureHTTPParser", "maxBodyLength", "maxContentLength", "maxRedirects", "method", "noRetry", "onDownloadProgress", "onUploadProgress", "params", "paramsSerializer", "proxy", "responseEncoding", "responseType", "retried", "signal", "socketPath", "timeout", "timeoutErrorMessage", "transformRequest", "transformResponse", "transitional", "url", "validateStatus", "withCredentials", "xsrfCookieName", "xsrfHeaderName"];
class RequestError extends Error {
  constructor(message, options) {
    super(message);
    this.code = void 0;
    this.status = void 0;
    this.url = void 0;
    this.method = void 0;
    /**
     * Should be serializable
     */
    this.config = void 0;
    this.code = options.code;
    this.url = options.url;
    this.method = options.method;
    this.status = options.status;
    this.config = options.config || {};
    this.name = 'RequestError';
    Object.setPrototypeOf(this, RequestError.prototype);
  }
  static fromAxiosError(error) {
    var _error$response, _error$config, _error$response2, _error$config2;
    // We want to support web and mobile custom configurations,
    // so we need to extract the properties that we want to keep by removing the ones we don't want.
    const _ref = (error == null || (_error$response = error.response) == null ? void 0 : _error$response.config) || {},
      config = _objectWithoutPropertiesLoose(_ref, _excluded$4);
    const requestError = new RequestError(error.message, {
      code: error.code,
      config: config,
      method: (_error$config = error.config) == null ? void 0 : _error$config.method,
      status: (_error$response2 = error.response) == null ? void 0 : _error$response2.status,
      url: (_error$config2 = error.config) == null ? void 0 : _error$config2.url
    });
    return requestError;
  }
  /**
   * The purpose here is to update the `message` property of the error
   * to be localized and user-friendly.
   * Setting the message is the responsibility of the app (web, admin-portal, mobile, etc.)
   * handled in the interceptors.
   *
   * @param message
   */
  setMessage(message) {
    this.message = message;
  }
}

const isResponse = response => !!(response != null && response.config);
/**
 * We map the backend response messages to buckets of errors based on the
 * error type. This is used to display the correct error message and format to the user.
 */
var ErrorClassification;
(function (ErrorClassification) {
  ErrorClassification["UNCLASSIFIED"] = "UNCLASSIFIED";
  ErrorClassification["NETWORK_SERVER_DOWN"] = "API_NETWORK_INFO_SERVER_DOWN";
  ErrorClassification["NETWORK_OFFLINE"] = "API_NETWORK_INFO_OFFLINE";
  ErrorClassification["ERR_CANCELED"] = "ERR_CANCELED";
})(ErrorClassification || (ErrorClassification = {}));
const handleResponseErrors = error => {
  const errorData = handleCommonStringErrors(error);
  if (isKnownError(errorData)) {
    return getKnownErrorMsg(errorData);
  }
  return ErrorClassification.UNCLASSIFIED;
};
const getResponseErrorMsg = response => {
  const {
    data,
    headers: {
      'content-type': contentType
    }
  } = response;
  if (contentType != null && contentType.includes('text/html')) {
    return data;
  }
  return handleResponseErrors(data);
};
const isValidationError = error => {
  var _error$validation;
  return !!((_error$validation = error.validation) != null && (_error$validation = _error$validation.body) != null && _error$validation.message);
};
const isErrorWithMessage = error => !!error.message;
const isKnownError = error => error && typeof error === 'object' && (isValidationError(error) || !!error.message || !!error.errors);
const getKnownErrorMsg = error => {
  if (isValidationError(error)) {
    return error.validation.body.message;
  }
  if (isErrorWithMessage(error)) {
    return error.message;
  }
  return error.errors.join('\n');
};
const handleCommonStringErrors = error => {
  const isNonStringError = typeof error === 'string' && error.startsWith('Non-string error: ');
  if (isNonStringError) {
    try {
      return JSON.parse(error.replace('Non-string error: ', ''));
    } catch (_unused) {
      return error;
    }
  }
  return error;
};
const parseResponseError = error => {
  const responseError = RequestError.fromAxiosError(error);
  // response: The request was made and the server responded with a status code
  // that falls out of the range of 2xx
  // There's also `request` and `config` which contains information about the request.
  // request: The request was made but no response was received
  // is an instance of XMLHttpRequest in the browser and an instance of
  // http.ClientRequest in node.js
  if (error.code === ErrorClassification.ERR_CANCELED) {
    return responseError;
  }
  responseError.setMessage(isResponse(error.response) ? getResponseErrorMsg(error.response) : ErrorClassification.UNCLASSIFIED);
  return responseError;
};

const axiosInstance = axios.create();
/**
 * This function be overwritten at run-time by each client.
 */
function setBaseUrl(url) {
  axiosInstance.defaults.baseURL = url;
}
const isAuthUrl = url => {
  if (!url) return false;
  return url.includes('auth') || url.includes('users/logout');
};
async function attemptRetry(error) {
  if (!isResponse(error.response) || error.response.status !== 401 || error.code === AxiosError.ERR_NETWORK) {
    throw error;
  }
  const response = error.response;
  const refreshToken = getRefreshToken();
  const {
    status,
    config
  } = response;
  const shouldRetry = !config.noRetry && status === 401 && !config.retried && response.data !== 'Account has been deactivated by admin';
  if (shouldRetry) {
    config.retried = true;
    const token = await memoizedNewToken(refreshToken || null);
    if (token) {
      config.headers = buildHeaders(config.headers, token);
      setToken(token);
      return await axios(config);
    }
  }
  if (!isAuthUrl(config.url)) {
    onLogout == null || onLogout();
  }
  throw error;
}
const interceptBeforeRetryHandlers = [];
const interceptAfterErrorHandlers = [];
async function interceptErrorResponse(error) {
  // Meta is used to pass data between handlers.
  // It is a mutable reference.
  const meta = new Map();
  try {
    for (const fn of interceptBeforeRetryHandlers) {
      const result = await fn(error, meta);
      if (result) {
        return result;
      }
    }
    return await attemptRetry(error);
  } catch (error) {
    if (error instanceof AxiosError) {
      const normalizedError = parseResponseError(error);
      for (const fn of interceptAfterErrorHandlers) {
        const result = await fn(normalizedError, meta);
        if (result) {
          return result;
        }
      }
      throw normalizedError;
    }
    throw error;
  }
}
function addBeforeRetryErrorInterceptor(fn) {
  interceptBeforeRetryHandlers.push(fn);
}
function addAfterRetryErrorInterceptor(fn) {
  interceptAfterErrorHandlers.push(fn);
}
const requestNewToken = async refreshToken => {
  if (!refreshToken) return null;
  try {
    const {
      data
    } = await axiosInstance.post('/pub/token', {
      refreshToken
    });
    return data.token;
  } catch (error) {
    return null;
  }
};
const memoizedNewToken = mem(requestNewToken, {
  maxAge: 1000 * 60 * 60 * 4
}); // four hours
const buildHeaders = (additionalHeaders, token) => _extends({
  Accept: 'application/json',
  'Content-Type': 'application/json',
  [requestIdHeaderName]: v4()
}, additionalHeaders, token ? {
  Authorization: `JWT ${token}`
} : {});
const logoutUserRequest = async (newToken, refreshToken) => {
  const token = newToken != null ? newToken : getToken();
  const refresh = refreshToken != null ? refreshToken : getRefreshToken();
  const headers = buildHeaders({}, token);
  return await axiosInstance.post('/users/logout', refresh ? {
    refreshToken: refresh
  } : {}, {
    headers
  });
};
const logoutUser = mem(logoutUserRequest, {
  maxAge: 5000
});
// Side-effect
axiosInstance.interceptors.response.use(response => response, interceptErrorResponse);

function getAuthorizationHeader() {
  const token = getToken();
  const headers = token ? {
    Authorization: `JWT ${token}`
  } : undefined;
  return headers;
}
async function sitFetch({
  url,
  method,
  body,
  headers: additionalHeaders,
  pathParams,
  queryParams,
  signal,
  requestConfig
}) {
  const token = getToken();
  const headers = _extends({}, await getHeaders(), additionalHeaders);
  const requestHeaders = buildHeaders(headers, token);
  // errors would be `AxiosError<_TError>`
  const data = body ? body instanceof FormData ? body : JSON.stringify(body) : undefined;
  const response = await axiosInstance.request(_extends({
    method: method.toUpperCase(),
    signal,
    url: resolveUrl(url, queryParams, pathParams),
    data,
    headers: requestHeaders
  }, requestConfig));
  return response.data;
}
function pruneUndefinedValues(obj) {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== undefined) acc[key] = value;
    return acc;
  }, {});
}
const resolveUrl = (url, queryParams = {}, pathParams = {}) => {
  const safeQueryParams = pruneUndefinedValues(queryParams);
  let query = stringify(safeQueryParams);
  if (query) query = `?${query}`;
  return url.replace(/{\w*}/g, key => pathParams[key.slice(1, -1)]) + query;
};

/**
 * Context injected into every react-query hook wrappers
 *
 * @param queryOptions options from the useQuery wrapper
 */
function useSitContext(_queryOptions) {
  const headers = getAuthorizationHeader();
  return {
    fetcherOptions: {
      headers
    },
    queryOptions: {},
    queryKeyFn
  };
}
const queryKeyFn = operation => {
  const queryKey = hasPathParams(operation) ? operation.path.split('/').filter(Boolean).map(i => resolvePathParam(i, operation.variables.pathParams)) : operation.path.split('/').filter(Boolean);
  if (hasQueryParams(operation)) {
    queryKey.push(operation.variables.queryParams);
  }
  if (hasBody(operation)) {
    queryKey.push(operation.variables.body);
  }
  return queryKey;
};
// Helpers
const resolvePathParam = (key, pathParams) => {
  if (key.startsWith('{') && key.endsWith('}')) {
    return pathParams[key.slice(1, -1)];
  }
  return key;
};
const hasPathParams = operation => {
  return Boolean(operation.variables.pathParams);
};
const hasBody = operation => {
  return Boolean(operation.variables.body);
};
const hasQueryParams = operation => {
  return Boolean(operation.variables.queryParams);
};

/**
 * Delete user account
 */
const fetchDeleteApiAccount = (variables, signal) => sitFetch(_extends({
  url: '/accounts/{accountId}',
  method: 'delete'
}, variables, {
  signal
}));
/**
 * Delete user account
 */
const useDeleteApiAccount = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchDeleteApiAccount(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Update user account
 */
const fetchPutApiAccount = (variables, signal) => sitFetch(_extends({
  url: '/accounts/{accountId}',
  method: 'put'
}, variables, {
  signal
}));
/**
 * Update user account
 */
const usePutApiAccount = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPutApiAccount(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get user accounts
 */
const fetchGetApiAccounts = (variables, signal) => sitFetch(_extends({
  url: '/accounts',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get user accounts
 */
const useGetApiAccounts = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/accounts',
      operationId: 'getApiAccounts',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiAccounts(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Get companies
 */
const fetchGetApiAdminCompanies = (variables, signal) => sitFetch(_extends({
  url: '/admin/companies',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get companies
 */
const useGetApiAdminCompanies = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/admin/companies',
      operationId: 'getApiAdminCompanies',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiAdminCompanies(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Get company by company tree settings id
 */
const fetchGetApiAdminCompanyByCompanyTreeSettingsId = (variables, signal) => sitFetch(_extends({
  url: '/admin/companies/{id}',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get company by company tree settings id
 */
const useGetApiAdminCompanyByCompanyTreeSettingsId = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/admin/companies/{id}',
      operationId: 'getApiAdminCompanyByCompanyTreeSettingsId',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiAdminCompanyByCompanyTreeSettingsId(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Patch company
 */
const fetchPatchApiAdminCompany = (variables, signal) => sitFetch(_extends({
  url: '/admin/companies/{id}',
  method: 'patch'
}, variables, {
  signal
}));
/**
 * Patch company
 */
const usePatchApiAdminCompany = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPatchApiAdminCompany(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Update company entity
 */
const fetchPatchApiAdminCompanyEntity = (variables, signal) => sitFetch(_extends({
  url: '/admin/company-entities/{id}',
  method: 'patch'
}, variables, {
  signal
}));
/**
 * Update company entity
 */
const usePatchApiAdminCompanyEntity = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPatchApiAdminCompanyEntity(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get company users
 */
const fetchGetApiAdminCompanyUsers = (variables, signal) => sitFetch(_extends({
  url: '/admin/companies/{id}/users',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get company users
 */
const useGetApiAdminCompanyUsers = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/admin/companies/{id}/users',
      operationId: 'getApiAdminCompanyUsers',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiAdminCompanyUsers(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Schedule delete company tree settings
 */
const fetchDeleteApiAdminCompanyTreeSettings = (variables, signal) => sitFetch(_extends({
  url: '/admin/company-tree-settings-by-external-unique-id/{id}',
  method: 'delete'
}, variables, {
  signal
}));
/**
 * Schedule delete company tree settings
 */
const useDeleteApiAdminCompanyTreeSettings = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchDeleteApiAdminCompanyTreeSettings(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get Intacct company info
 */
const fetchGetApiAdminIntacctCompanyInfo = (variables, signal) => sitFetch(_extends({
  url: '/admin/intacct-company-info/{intacctCompanyId}',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get Intacct company info
 */
const useGetApiAdminIntacctCompanyInfo = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/admin/intacct-company-info/{intacctCompanyId}',
      operationId: 'getApiAdminIntacctCompanyInfo',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiAdminIntacctCompanyInfo(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Check if user is authenticated
 */
const fetchGetApiAdminIsAuthenticated = (variables, signal) => sitFetch(_extends({
  url: '/admin/is-authenticated',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Check if user is authenticated
 */
const useGetApiAdminIsAuthenticated = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/admin/is-authenticated',
      operationId: 'getApiAdminIsAuthenticated',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiAdminIsAuthenticated(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Get job status
 */
const fetchGetApiAdminJobStatus = (variables, signal) => sitFetch(_extends({
  url: '/admin/job-status',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get job status
 */
const useGetApiAdminJobStatus = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/admin/job-status',
      operationId: 'getApiAdminJobStatus',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiAdminJobStatus(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Provision company
 */
const fetchPostApiAdminProvision = (variables, signal) => sitFetch(_extends({
  url: '/admin/provision',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Provision company
 */
const usePostApiAdminProvision = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiAdminProvision(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * List trigger package installations
 */
const fetchGetApiAdminTriggersPackages = (variables, signal) => sitFetch(_extends({
  url: '/admin/triggers-packages',
  method: 'get'
}, variables, {
  signal
}));
/**
 * List trigger package installations
 */
const useGetApiAdminTriggersPackages = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/admin/triggers-packages',
      operationId: 'getApiAdminTriggersPackages',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiAdminTriggersPackages(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Install trigger package
 */
const fetchPostApiAdminTriggersPackages = (variables, signal) => sitFetch(_extends({
  url: '/admin/triggers-packages',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Install trigger package
 */
const usePostApiAdminTriggersPackages = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiAdminTriggersPackages(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get scheduled jobs
 */
const fetchGetApiAdminScheduledJobs = (variables, signal) => sitFetch(_extends({
  url: '/admin/scheduled-jobs',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get scheduled jobs
 */
const useGetApiAdminScheduledJobs = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/admin/scheduled-jobs',
      operationId: 'getApiAdminScheduledJobs',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiAdminScheduledJobs(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Schedule job
 */
const fetchPostApiAdminScheduleJob = (variables, signal) => sitFetch(_extends({
  url: '/admin/scheduled-jobs',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Schedule job
 */
const usePostApiAdminScheduleJob = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiAdminScheduleJob(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get sync status for company
 */
const fetchGetApiAdminSyncStatus = (variables, signal) => sitFetch(_extends({
  url: '/admin/sync-status/{companyTreeSettingsId}',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get sync status for company
 */
const useGetApiAdminSyncStatus = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/admin/sync-status/{companyTreeSettingsId}',
      operationId: 'getApiAdminSyncStatus',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiAdminSyncStatus(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Initialize sync tree job for company
 */
const fetchPostApiAdminSyncTree = (variables, signal) => sitFetch(_extends({
  url: '/admin/sync-tree',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Initialize sync tree job for company
 */
const usePostApiAdminSyncTree = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiAdminSyncTree(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Sync trigger packages
 */
const fetchPostApiAdminSyncTriggersPackages = (variables, signal) => sitFetch(_extends({
  url: '/admin/sync-triggers-packages/{companyTreeSettingsId}',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Sync trigger packages
 */
const usePostApiAdminSyncTriggersPackages = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiAdminSyncTriggersPackages(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get user by user ID
 */
const fetchGetApiAdminUserById = (variables, signal) => sitFetch(_extends({
  url: '/admin/users/{id}',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get user by user ID
 */
const useGetApiAdminUserById = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/admin/users/{id}',
      operationId: 'getApiAdminUserById',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiAdminUserById(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Set user password
 */
const fetchPostApiAdminUserSetPassword = (variables, signal) => sitFetch(_extends({
  url: '/admin/users/{id}/set-password',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Set user password
 */
const usePostApiAdminUserSetPassword = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiAdminUserSetPassword(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Decline timesheet entries asynchronously
 */
const fetchPutApiApprovalsApproveAsync = (variables, signal) => sitFetch(_extends({
  url: '/approvals/approve-async',
  method: 'put'
}, variables, {
  signal
}));
/**
 * Decline timesheet entries asynchronously
 */
const usePutApiApprovalsApproveAsync = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPutApiApprovalsApproveAsync(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Decinle timesheet entries asynchronously
 */
const fetchPutApiApprovalsDeclineAsync = (variables, signal) => sitFetch(_extends({
  url: '/approvals/decline-async',
  method: 'put'
}, variables, {
  signal
}));
/**
 * Decinle timesheet entries asynchronously
 */
const usePutApiApprovalsDeclineAsync = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPutApiApprovalsDeclineAsync(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get approvals check
 */
const fetchGetApiApprovalsCheck = (variables, signal) => sitFetch(_extends({
  url: '/approvals/check',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get approvals check
 */
const useGetApiApprovalsCheck = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/approvals/check',
      operationId: 'getApiApprovalsCheck',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiApprovalsCheck(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Get timesheet approvals
 */
const fetchGetApiApprovalsTimesheets = (variables, signal) => sitFetch(_extends({
  url: '/approvals/timesheets',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get timesheet approvals
 */
const useGetApiApprovalsTimesheets = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/approvals/timesheets',
      operationId: 'getApiApprovalsTimesheets',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiApprovalsTimesheets(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Get timesheet entries to approve
 */
const fetchGetApiApprovalsEntries = (variables, signal) => sitFetch(_extends({
  url: '/approvals/entries',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get timesheet entries to approve
 */
const useGetApiApprovalsEntries = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/approvals/entries',
      operationId: 'getApiApprovalsEntries',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiApprovalsEntries(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * When a user access a SIT iframe from Intacct, Intacct's server will send a request to this endpoint with the Intacct access token. This endpoint returns a SIT access token that can be used to access SIT. The SIT access token is then added to a fragment in the URL of the SIT iframe, where it is used to authenticate the user by forwarding the token to the SIT server. The SIT access token is a JWT token that contains the identity of the user, the current entity selected on Intacct, and the expiration time of the token.
 */
const fetchPostApiAuthIntacctAccessToken = (variables, signal) => sitFetch(_extends({
  url: '/auth/intacct-access-token',
  method: 'post'
}, variables, {
  signal
}));
/**
 * When a user access a SIT iframe from Intacct, Intacct's server will send a request to this endpoint with the Intacct access token. This endpoint returns a SIT access token that can be used to access SIT. The SIT access token is then added to a fragment in the URL of the SIT iframe, where it is used to authenticate the user by forwarding the token to the SIT server. The SIT access token is a JWT token that contains the identity of the user, the current entity selected on Intacct, and the expiration time of the token.
 */
const usePostApiAuthIntacctAccessToken = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiAuthIntacctAccessToken(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Login to SIT with Intacct credentials
 */
const fetchPostApiAuthLogin = (variables, signal) => sitFetch(_extends({
  url: '/auth/login',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Login to SIT with Intacct credentials
 */
const usePostApiAuthLogin = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiAuthLogin(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Login with biometrics
 */
const fetchPostApiAuthLoginBiometrics = (variables, signal) => sitFetch(_extends({
  url: '/auth/login/biometrics',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Login with biometrics
 */
const usePostApiAuthLoginBiometrics = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiAuthLoginBiometrics(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Submit MFA fallback code
 */
const fetchPostApiAuthMfaFallback = (variables, signal) => sitFetch(_extends({
  url: '/auth/mfa-fallback',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Submit MFA fallback code
 */
const usePostApiAuthMfaFallback = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiAuthMfaFallback(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Add or remove a calendar from the user's calendar list
 */
const fetchPutApiCalendarCalendarsId = (variables, signal) => sitFetch(_extends({
  url: '/calendar/calendars/{id}',
  method: 'put'
}, variables, {
  signal
}));
/**
 * Add or remove a calendar from the user's calendar list
 */
const usePutApiCalendarCalendarsId = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPutApiCalendarCalendarsId(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get clients
 */
const fetchGetApiClients = (variables, signal) => sitFetch(_extends({
  url: '/clients',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get clients
 */
const useGetApiClients = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/clients',
      operationId: 'getApiClients',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiClients(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Delete cluster node
 */
const fetchDeleteApiCluster = (variables, signal) => sitFetch(_extends({
  url: '/clusters/{id}',
  method: 'delete'
}, variables, {
  signal
}));
/**
 * Delete cluster node
 */
const useDeleteApiCluster = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchDeleteApiCluster(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Update cluster node
 */
const fetchPutApiCluster = (variables, signal) => sitFetch(_extends({
  url: '/clusters/{id}',
  method: 'put'
}, variables, {
  signal
}));
/**
 * Update cluster node
 */
const usePutApiCluster = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPutApiCluster(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Split a cluster
 */
const fetchPostApiClusterSplit = (variables, signal) => sitFetch(_extends({
  url: '/clusters/{id}/split',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Split a cluster
 */
const usePostApiClusterSplit = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiClusterSplit(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Delete cluster node
 */
const fetchDeleteApiClusterNode = (variables, signal) => sitFetch(_extends({
  url: '/clusters/{id}/nodes/{nodeId}',
  method: 'delete'
}, variables, {
  signal
}));
/**
 * Delete cluster node
 */
const useDeleteApiClusterNode = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchDeleteApiClusterNode(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Update cluster node
 */
const fetchPutApiClusterNode = (variables, signal) => sitFetch(_extends({
  url: '/clusters/{id}/nodes/{nodeId}',
  method: 'put'
}, variables, {
  signal
}));
/**
 * Update cluster node
 */
const usePutApiClusterNode = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPutApiClusterNode(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Update overlaps
 */
const fetchPutApiClusterNodeOverlaps = (variables, signal) => sitFetch(_extends({
  url: '/clusters/nodes/overlaps',
  method: 'put'
}, variables, {
  signal
}));
/**
 * Update overlaps
 */
const usePutApiClusterNodeOverlaps = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPutApiClusterNodeOverlaps(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get clusters for a timesheet
 */
const fetchGetApiClustersForTimesheet = (variables, signal) => sitFetch(_extends({
  url: '/clusters/timesheet/{timesheetId}',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get clusters for a timesheet
 */
const useGetApiClustersForTimesheet = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/clusters/timesheet/{timesheetId}',
      operationId: 'getApiClustersForTimesheet',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiClustersForTimesheet(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Get Intacct entity (SIT company)
 */
const fetchGetApiCompany = (variables, signal) => sitFetch(_extends({
  url: '/companies',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get Intacct entity (SIT company)
 */
const useGetApiCompany = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/companies',
      operationId: 'getApiCompany',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiCompany(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Update company
 */
const fetchPutApiCompaniesByUuid = (variables, signal) => sitFetch(_extends({
  url: '/companies/{companyId}',
  method: 'put'
}, variables, {
  signal
}));
/**
 * Update company
 */
const usePutApiCompaniesByUuid = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPutApiCompaniesByUuid(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get data status
 */
const fetchGetApiDataStatus = (variables, signal) => sitFetch(_extends({
  url: '/data-status',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get data status
 */
const useGetApiDataStatus = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/data-status',
      operationId: 'getApiDataStatus',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiDataStatus(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Get dimensions
 */
const fetchGetApiDimensions = (variables, signal) => sitFetch(_extends({
  url: '/dimensions',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get dimensions
 */
const useGetApiDimensions = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/dimensions',
      operationId: 'getApiDimensions',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiDimensions(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Get employees
 */
const fetchGetApiEmployees = (variables, signal) => sitFetch(_extends({
  url: '/employees',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get employees
 */
const useGetApiEmployees = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/employees',
      operationId: 'getApiEmployees',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiEmployees(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Run intacct sync
 */
const fetchGetApiIntacctRunSync = (variables, signal) => sitFetch(_extends({
  url: '/intacct/run-sync',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Run intacct sync
 */
const useGetApiIntacctRunSync = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/intacct/run-sync',
      operationId: 'getApiIntacctRunSync',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiIntacctRunSync(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Run intacct sync
 */
const fetchPostApiIntacctRunSync = (variables, signal) => sitFetch(_extends({
  url: '/intacct/run-sync',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Run intacct sync
 */
const usePostApiIntacctRunSync = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiIntacctRunSync(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Run Intacct timesheets sync
 */
const fetchGetApiIntacctRunTimesheetsSync = (variables, signal) => sitFetch(_extends({
  url: '/intacct/run-timesheets-sync',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Run Intacct timesheets sync
 */
const useGetApiIntacctRunTimesheetsSync = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/intacct/run-timesheets-sync',
      operationId: 'getApiIntacctRunTimesheetsSync',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiIntacctRunTimesheetsSync(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Run Intacct timesheets sync
 */
const fetchPostApiIntacctRunTimesheetsSync = (variables, signal) => sitFetch(_extends({
  url: '/intacct/run-timesheets-sync',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Run Intacct timesheets sync
 */
const usePostApiIntacctRunTimesheetsSync = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiIntacctRunTimesheetsSync(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get Intacct company sync status
 */
const fetchGetApiIntacctSyncStatus = (variables, signal) => sitFetch(_extends({
  url: '/intacct/sync-status',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get Intacct company sync status
 */
const useGetApiIntacctSyncStatus = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/intacct/sync-status',
      operationId: 'getApiIntacctSyncStatus',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiIntacctSyncStatus(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Get Intacct timesheets sync status
 */
const fetchGetApiIntacctSyncTimesheetsStatus = (variables, signal) => sitFetch(_extends({
  url: '/intacct/sync-timesheets-status',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get Intacct timesheets sync status
 */
const useGetApiIntacctSyncTimesheetsStatus = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/intacct/sync-timesheets-status',
      operationId: 'getApiIntacctSyncTimesheetsStatus',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiIntacctSyncTimesheetsStatus(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * When a user access a SIT iframe from Intacct, Intacct's server will send a request to this endpoint with the Intacct access token. This endpoint returns a SIT access token that can be used to access SIT. The SIT access token is then added to a fragment in the URL of the SIT iframe, where it is used to authenticate the user by forwarding the token to the SIT server. The SIT access token is a JWT token that contains the identity of the user, the current entity selected on Intacct, and the expiration time of the token.
 */
const fetchPostApiIntacctIntegrationAccessToken = (variables, signal) => sitFetch(_extends({
  url: '/intacct-integration/access-token',
  method: 'post'
}, variables, {
  signal
}));
/**
 * When a user access a SIT iframe from Intacct, Intacct's server will send a request to this endpoint with the Intacct access token. This endpoint returns a SIT access token that can be used to access SIT. The SIT access token is then added to a fragment in the URL of the SIT iframe, where it is used to authenticate the user by forwarding the token to the SIT server. The SIT access token is a JWT token that contains the identity of the user, the current entity selected on Intacct, and the expiration time of the token.
 */
const usePostApiIntacctIntegrationAccessToken = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiIntacctIntegrationAccessToken(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * When an intacct configuration changes (Project configuraiton, for example) and the trigger is not available, Intacct can send a message to SIT to notify the change. This route is used to handle all types of changes.
 */
const fetchPostApiIntacctIntegrationHandleChange = (variables, signal) => sitFetch(_extends({
  url: '/intacct-integration/handle-change',
  method: 'post'
}, variables, {
  signal
}));
/**
 * When an intacct configuration changes (Project configuraiton, for example) and the trigger is not available, Intacct can send a message to SIT to notify the change. This route is used to handle all types of changes.
 */
const usePostApiIntacctIntegrationHandleChange = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiIntacctIntegrationHandleChange(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * On Intacct, when an admin clicks the switch to enable SIT, the following occurs:
 *   1. Intacct creates a set of WSU credentials specifically for SIT
 *   2. Intacct sends a signed message with the WSU credentials (this route)
 *   3. SIT receives the message and verifies the signature
 *   4. SIT schedules a job to provision the company, returning the job information
 *   5. SIT provisions the company
 *   6. (Optional) SIT calls an Intacct endpoint specified in `callback` when the job is complete
 */
const fetchPostApiIntacctIntegrationProvision = (variables, signal) => sitFetch(_extends({
  url: '/intacct-integration/provision',
  method: 'post'
}, variables, {
  signal
}));
/**
 * On Intacct, when an admin clicks the switch to enable SIT, the following occurs:
 *   1. Intacct creates a set of WSU credentials specifically for SIT
 *   2. Intacct sends a signed message with the WSU credentials (this route)
 *   3. SIT receives the message and verifies the signature
 *   4. SIT schedules a job to provision the company, returning the job information
 *   5. SIT provisions the company
 *   6. (Optional) SIT calls an Intacct endpoint specified in `callback` when the job is complete
 */
const usePostApiIntacctIntegrationProvision = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiIntacctIntegrationProvision(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * On Intacct, when an admin clicks the switch to enable SIT, the following occurs:
 *   1. Intacct creates a set of WSU credentials specifically for SIT
 *   2. Intacct sends a signed message with the WSU credentials
 *   3. SIT receives the message and verifies the signature
 *   4. SIT schedules a job to provision the company, returning the job information
 *   5. SIT provisions the company
 *   6. (Optional) SIT calls an Intacct endpoint specified in `callback` when the job is complete
 *
 * This route is called by Intacct to check the status of the job. Since we cannot send a JWT token in the request header,
 */
const fetchGetApiIntacctIntegrationProvisionJobStatus = (variables, signal) => sitFetch(_extends({
  url: '/intacct-integration/provision',
  method: 'get'
}, variables, {
  signal
}));
/**
 * On Intacct, when an admin clicks the switch to enable SIT, the following occurs:
 *   1. Intacct creates a set of WSU credentials specifically for SIT
 *   2. Intacct sends a signed message with the WSU credentials
 *   3. SIT receives the message and verifies the signature
 *   4. SIT schedules a job to provision the company, returning the job information
 *   5. SIT provisions the company
 *   6. (Optional) SIT calls an Intacct endpoint specified in `callback` when the job is complete
 *
 * This route is called by Intacct to check the status of the job. Since we cannot send a JWT token in the request header,
 */
const useGetApiIntacctIntegrationProvisionJobStatus = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/intacct-integration/provision',
      operationId: 'getApiIntacctIntegrationProvisionJobStatus',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiIntacctIntegrationProvisionJobStatus(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * When a user accesses a SIT iframe from Intacct, Intacct's server will send a request to this endpoint with the user's Intacct REST credential using an access token and the OID. This endpoint returns nothing, it's a fire-and-forget operation.
 */
const fetchPostApiIntacctIntegrationUserRestToken = (variables, signal) => sitFetch(_extends({
  url: '/intacct-integration/user-rest-token',
  method: 'post'
}, variables, {
  signal
}));
/**
 * When a user accesses a SIT iframe from Intacct, Intacct's server will send a request to this endpoint with the user's Intacct REST credential using an access token and the OID. This endpoint returns nothing, it's a fire-and-forget operation.
 */
const usePostApiIntacctIntegrationUserRestToken = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiIntacctIntegrationUserRestToken(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get items
 */
const fetchGetApiItems = (variables, signal) => sitFetch(_extends({
  url: '/items',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get items
 */
const useGetApiItems = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/items',
      operationId: 'getApiItems',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiItems(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Get notifications
 */
const fetchGetApiNotifications = (variables, signal) => sitFetch(_extends({
  url: '/notifications',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get notifications
 */
const useGetApiNotifications = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/notifications',
      operationId: 'getApiNotifications',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiNotifications(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Mark all notifications as read
 */
const fetchPostApiNotificationsReadAll = (variables, signal) => sitFetch(_extends({
  url: '/notifications/read-all',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Mark all notifications as read
 */
const usePostApiNotificationsReadAll = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiNotificationsReadAll(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get projects
 */
const fetchGetApiProjects = (variables, signal) => sitFetch(_extends({
  url: '/projects',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get projects
 */
const useGetApiProjects = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/projects',
      operationId: 'getApiProjects',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiProjects(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Employee signup
 */
const fetchPostApiPubEmployeeSignup = (variables, signal) => sitFetch(_extends({
  url: '/pub/employee/signup',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Employee signup
 */
const usePostApiPubEmployeeSignup = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiPubEmployeeSignup(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Forgot password
 */
const fetchPostApiForgotPassword = (variables, signal) => sitFetch(_extends({
  url: '/pub/forgot',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Forgot password
 */
const usePostApiForgotPassword = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiForgotPassword(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get invite by token
 */
const fetchGetApiPubInvitesInviteToken = (variables, signal) => sitFetch(_extends({
  url: '/pub/invites/{inviteToken}',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get invite by token
 */
const useGetApiPubInvitesInviteToken = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/pub/invites/{inviteToken}',
      operationId: 'getApiPubInvitesInviteToken',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiPubInvitesInviteToken(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Validate reset token
 */
const fetchGetApiValidateResetToken = (variables, signal) => sitFetch(_extends({
  url: '/pub/reset/{resetToken}',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Validate reset token
 */
const useGetApiValidateResetToken = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/pub/reset/{resetToken}',
      operationId: 'getApiValidateResetToken',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiValidateResetToken(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Reset password
 */
const fetchPostApiResetPassword = (variables, signal) => sitFetch(_extends({
  url: '/pub/reset/{resetToken}',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Reset password
 */
const usePostApiResetPassword = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiResetPassword(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Post Api Pub Token
 */
const fetchPostApiPubToken = (variables, signal) => sitFetch(_extends({
  url: '/pub/token',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Post Api Pub Token
 */
const usePostApiPubToken = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiPubToken(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Post Api Pub Verify
 */
const fetchPostApiPubVerify = (variables, signal) => sitFetch(_extends({
  url: '/pub/verify',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Post Api Pub Verify
 */
const usePostApiPubVerify = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiPubVerify(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Health check endpoint for the API
 */
const fetchGetApiServiceHealth = (variables, signal) => sitFetch(_extends({
  url: '/service/health',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Health check endpoint for the API
 */
const useGetApiServiceHealth = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/service/health',
      operationId: 'getApiServiceHealth',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiServiceHealth(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Get tasks
 */
const fetchGetApiTasks = (variables, signal) => sitFetch(_extends({
  url: '/tasks',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get tasks
 */
const useGetApiTasks = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/tasks',
      operationId: 'getApiTasks',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiTasks(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Get team
 */
const fetchGetApiTeam = (variables, signal) => sitFetch(_extends({
  url: '/team',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get team
 */
const useGetApiTeam = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/team',
      operationId: 'getApiTeam',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiTeam(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Invite team member
 */
const fetchPostApiTeamByUuidInvite = (variables, signal) => sitFetch(_extends({
  url: '/team/{teamId}/invite',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Invite team member
 */
const usePostApiTeamByUuidInvite = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiTeamByUuidInvite(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Invite multiple team members
 */
const fetchPostApiTeamBulkInvite = (variables, signal) => sitFetch(_extends({
  url: '/team/bulk-invite',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Invite multiple team members
 */
const usePostApiTeamBulkInvite = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiTeamBulkInvite(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get Api Time Assistant Download Links
 */
const fetchGetApiTimeAssistantDownloadLinks = (variables, signal) => sitFetch(_extends({
  url: '/time-assistant/download-links',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get Api Time Assistant Download Links
 */
const useGetApiTimeAssistantDownloadLinks = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/time-assistant/download-links',
      operationId: 'getApiTimeAssistantDownloadLinks',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiTimeAssistantDownloadLinks(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Get time types
 */
const fetchGetApiTimeTypes = (variables, signal) => sitFetch(_extends({
  url: '/time-types',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get time types
 */
const useGetApiTimeTypes = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/time-types',
      operationId: 'getApiTimeTypes',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiTimeTypes(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Get timesheets
 */
const fetchGetApiTimesheets = (variables, signal) => sitFetch(_extends({
  url: '/timesheets',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get timesheets
 */
const useGetApiTimesheets = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/timesheets',
      operationId: 'getApiTimesheets',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiTimesheets(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Create a new timesheet
 */
const fetchPostApiTimesheets = (variables, signal) => sitFetch(_extends({
  url: '/timesheets',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Create a new timesheet
 */
const usePostApiTimesheets = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiTimesheets(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Delete timesheet
 */
const fetchPostApiTimesheetsDelete = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/delete',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Delete timesheet
 */
const usePostApiTimesheetsDelete = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiTimesheetsDelete(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get timesheet
 */
const fetchGetApiTimesheetsByUuid = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get timesheet
 */
const useGetApiTimesheetsByUuid = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/timesheets/{id}',
      operationId: 'getApiTimesheetsByUuid',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiTimesheetsByUuid(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Update timesheet
 */
const fetchPutApiTimesheetsByUuid = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}',
  method: 'put'
}, variables, {
  signal
}));
/**
 * Update timesheet
 */
const usePutApiTimesheetsByUuid = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPutApiTimesheetsByUuid(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * List attachments for a timesheet
 */
const fetchGetApiTimesheetsAttachmentByUuid = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}/attachments',
  method: 'get'
}, variables, {
  signal
}));
/**
 * List attachments for a timesheet
 */
const useGetApiTimesheetsAttachmentByUuid = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/timesheets/{id}/attachments',
      operationId: 'getApiTimesheetsAttachmentByUuid',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiTimesheetsAttachmentByUuid(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Create (and update) a timesheet attachment
 */
const fetchPostApiTimesheetsAttachmentByUuid = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}/attachments',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Create (and update) a timesheet attachment
 */
const usePostApiTimesheetsAttachmentByUuid = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiTimesheetsAttachmentByUuid(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Delete a timesheet attachment
 */
const fetchDeleteApiTimesheetsAttachmentByUuid = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}/attachments',
  method: 'delete'
}, variables, {
  signal
}));
/**
 * Delete a timesheet attachment
 */
const useDeleteApiTimesheetsAttachmentByUuid = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchDeleteApiTimesheetsAttachmentByUuid(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Download a timesheet attachment file
 */
const fetchGetApiTimesheetAttachmentFile = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}/attachments/download',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Download a timesheet attachment file
 */
const useGetApiTimesheetAttachmentFile = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/timesheets/{id}/attachments/download',
      operationId: 'getApiTimesheetAttachmentFile',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiTimesheetAttachmentFile(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Get a SIT timesheet ID by Intacct Entity ID and External Unique ID (used in embedded)
 */
const fetchGetApiTimesheetsByExternalUniqueId = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/external-unique-id/{intacctEntityId}/{externalUniqueId}',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get a SIT timesheet ID by Intacct Entity ID and External Unique ID (used in embedded)
 */
const useGetApiTimesheetsByExternalUniqueId = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/timesheets/external-unique-id/{intacctEntityId}/{externalUniqueId}',
      operationId: 'getApiTimesheetsByExternalUniqueId',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiTimesheetsByExternalUniqueId(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Duplicate a timesheet
 */
const fetchPostApiTimesheetsDuplicate = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}/duplicate',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Duplicate a timesheet
 */
const usePostApiTimesheetsDuplicate = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiTimesheetsDuplicate(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get timesheet entries for timesheet
 */
const fetchGetApiTimesheetsByUuidEntries = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}/entries',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get timesheet entries for timesheet
 */
const useGetApiTimesheetsByUuidEntries = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/timesheets/{id}/entries',
      operationId: 'getApiTimesheetsByUuidEntries',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiTimesheetsByUuidEntries(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Create a timesheet entry
 */
const fetchPostApiTimesheetsByUuidEntries = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}/entries',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Create a timesheet entry
 */
const usePostApiTimesheetsByUuidEntries = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiTimesheetsByUuidEntries(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get a timesheet entry
 */
const fetchGetApiTimesheetsEntryById = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}/entries/{entryId}',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get a timesheet entry
 */
const useGetApiTimesheetsEntryById = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/timesheets/{id}/entries/{entryId}',
      operationId: 'getApiTimesheetsEntryById',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiTimesheetsEntryById(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Create a timesheet entry
 */
const fetchPutApiTimesheetsByUuidEntriesByEntryId = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}/entries/{entryId}',
  method: 'put'
}, variables, {
  signal
}));
/**
 * Create a timesheet entry
 */
const usePutApiTimesheetsByUuidEntriesByEntryId = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPutApiTimesheetsByUuidEntriesByEntryId(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Check whether approve and declined are allowed for a specific entry
 */
const fetchGetApiTimesheetsEntryApproveDeclineAllowed = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}/entries/{entryId}/approve-decline-allowed',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Check whether approve and declined are allowed for a specific entry
 */
const useGetApiTimesheetsEntryApproveDeclineAllowed = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/timesheets/{id}/entries/{entryId}/approve-decline-allowed',
      operationId: 'getApiTimesheetsEntryApproveDeclineAllowed',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiTimesheetsEntryApproveDeclineAllowed(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Recall timesheet
 */
const fetchPostApiTimesheetsByUuidRecall = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}/recall',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Recall timesheet
 */
const usePostApiTimesheetsByUuidRecall = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiTimesheetsByUuidRecall(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get timesheet entries and associated metadata
 */
const fetchGetApiTimesheetTimeEntries = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}/time-entries',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get timesheet entries and associated metadata
 */
const useGetApiTimesheetTimeEntries = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/timesheets/{id}/time-entries',
      operationId: 'getApiTimesheetTimeEntries',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiTimesheetTimeEntries(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Get timesheet rows for timesheet
 */
const fetchGetApiTimesheetsByUuidRows = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}/rows',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get timesheet rows for timesheet
 */
const useGetApiTimesheetsByUuidRows = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/timesheets/{id}/rows',
      operationId: 'getApiTimesheetsByUuidRows',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiTimesheetsByUuidRows(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Create timesheet row
 */
const fetchPostApiTimesheetsByUuidRows = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}/rows',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Create timesheet row
 */
const usePostApiTimesheetsByUuidRows = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiTimesheetsByUuidRows(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Delete timesheet row
 */
const fetchDeleteApiTimesheetsByUuidRows = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}/rows/{timesheetRowId}',
  method: 'delete'
}, variables, {
  signal
}));
/**
 * Delete timesheet row
 */
const useDeleteApiTimesheetsByUuidRows = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchDeleteApiTimesheetsByUuidRows(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Update timesheet row
 */
const fetchPutApiTimesheetsByUuidRows = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}/rows/{timesheetRowId}',
  method: 'put'
}, variables, {
  signal
}));
/**
 * Update timesheet row
 */
const usePutApiTimesheetsByUuidRows = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPutApiTimesheetsByUuidRows(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Update timesheet row billable
 */
const fetchPutApiTimesheetsByUuidRowsBillable = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}/rows/{timesheetRowId}/billable',
  method: 'put'
}, variables, {
  signal
}));
/**
 * Update timesheet row billable
 */
const usePutApiTimesheetsByUuidRowsBillable = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPutApiTimesheetsByUuidRowsBillable(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Update timesheet row dimension values
 */
const fetchPutApiTimesheetsByUuidRowsDimensionValues = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}/rows/{timesheetRowId}/dimension-values',
  method: 'put'
}, variables, {
  signal
}));
/**
 * Update timesheet row dimension values
 */
const usePutApiTimesheetsByUuidRowsDimensionValues = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPutApiTimesheetsByUuidRowsDimensionValues(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Save timesheet using a background job
 */
const fetchPostApiTimesheetsByUuidSaveAsync = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}/save-async',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Save timesheet using a background job
 */
const usePostApiTimesheetsByUuidSaveAsync = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiTimesheetsByUuidSaveAsync(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Submit timesheet using a background job
 */
const fetchPostApiTimesheetsByUuidSubmitAsync = (variables, signal) => sitFetch(_extends({
  url: '/timesheets/{id}/submit-async',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Submit timesheet using a background job
 */
const usePostApiTimesheetsByUuidSubmitAsync = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiTimesheetsByUuidSubmitAsync(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Save whether the user has downloaded the Time Assistant
 */
const fetchPutApiUserActions = (variables, signal) => sitFetch(_extends({
  url: '/user-actions',
  method: 'put'
}, variables, {
  signal
}));
/**
 * Save whether the user has downloaded the Time Assistant
 */
const usePutApiUserActions = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPutApiUserActions(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Record user consents
 */
const fetchPostApiUserConsents = (variables, signal) => sitFetch(_extends({
  url: '/user-consents',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Record user consents
 */
const usePostApiUserConsents = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiUserConsents(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Revoke user's consent for either email or push
 */
const fetchPostApiUserConsentsRevoke = (variables, signal) => sitFetch(_extends({
  url: '/user-consents/revoke',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Revoke user's consent for either email or push
 */
const usePostApiUserConsentsRevoke = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiUserConsentsRevoke(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get user notification settings
 */
const fetchGetApiUserNotificationSettings = (variables, signal) => sitFetch(_extends({
  url: '/user-notification-settings',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get user notification settings
 */
const useGetApiUserNotificationSettings = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/user-notification-settings',
      operationId: 'getApiUserNotificationSettings',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiUserNotificationSettings(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Update user notification settings
 */
const fetchPutApiUserNotificationSettings = (variables, signal) => sitFetch(_extends({
  url: '/user-notification-settings',
  method: 'put'
}, variables, {
  signal
}));
/**
 * Update user notification settings
 */
const usePutApiUserNotificationSettings = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPutApiUserNotificationSettings(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get user settings
 */
const fetchGetApiUserSettings = (variables, signal) => sitFetch(_extends({
  url: '/user-settings',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get user settings
 */
const useGetApiUserSettings = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/user-settings',
      operationId: 'getApiUserSettings',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiUserSettings(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Update user settings
 */
const fetchPutApiUserSettings = (variables, signal) => sitFetch(_extends({
  url: '/user-settings',
  method: 'put'
}, variables, {
  signal
}));
/**
 * Update user settings
 */
const usePutApiUserSettings = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPutApiUserSettings(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Get current user
 */
const fetchGetApiUser = (variables, signal) => sitFetch(_extends({
  url: '/users',
  method: 'get'
}, variables, {
  signal
}));
/**
 * Get current user
 */
const useGetApiUser = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/users',
      operationId: 'getApiUser',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiUser(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * List users
 */
const fetchGetApiUsers = (variables, signal) => sitFetch(_extends({
  url: '/users/list',
  method: 'get'
}, variables, {
  signal
}));
/**
 * List users
 */
const useGetApiUsers = (variables, options) => {
  const {
    fetcherOptions,
    queryOptions,
    queryKeyFn
  } = useSitContext();
  return reactQuery.useQuery(_extends({
    queryKey: queryKeyFn({
      path: '/users/list',
      operationId: 'getApiUsers',
      variables
    }),
    queryFn: ({
      signal
    }) => fetchGetApiUsers(_extends({}, fetcherOptions, variables), signal)
  }, options, queryOptions));
};
/**
 * Logout
 */
const fetchPostApiUsersLogout = (variables, signal) => sitFetch(_extends({
  url: '/users/logout',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Logout
 */
const usePostApiUsersLogout = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiUsersLogout(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Update user
 */
const fetchPutApiUsersByUuid = (variables, signal) => sitFetch(_extends({
  url: '/users/{userId}',
  method: 'put'
}, variables, {
  signal
}));
/**
 * Update user
 */
const usePutApiUsersByUuid = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPutApiUsersByUuid(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Activate 2FA
 */
const fetchPostApiUsersActivate2Fa = (variables, signal) => sitFetch(_extends({
  url: '/users/activate2fa',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Activate 2FA
 */
const usePostApiUsersActivate2Fa = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiUsersActivate2Fa(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Change password
 */
const fetchPostApiUsersChangePassword = (variables, signal) => sitFetch(_extends({
  url: '/users/change-password',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Change password
 */
const usePostApiUsersChangePassword = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiUsersChangePassword(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Deactivate 2FA
 */
const fetchPostApiUsersDeactivate2Fa = (variables, signal) => sitFetch(_extends({
  url: '/users/deactivate2fa',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Deactivate 2FA
 */
const usePostApiUsersDeactivate2Fa = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiUsersDeactivate2Fa(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Verify password (during MFA initialization)
 */
const fetchPostApiUsersVerifyPassword = (variables, signal) => sitFetch(_extends({
  url: '/users/verify-password',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Verify password (during MFA initialization)
 */
const usePostApiUsersVerifyPassword = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiUsersVerifyPassword(_extends({}, fetcherOptions, variables))
  }, options));
};
/**
 * Verify 2FA
 */
const fetchPostApiUsersVerify2Fa = (variables, signal) => sitFetch(_extends({
  url: '/users/verify2fa',
  method: 'post'
}, variables, {
  signal
}));
/**
 * Verify 2FA
 */
const usePostApiUsersVerify2Fa = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(_extends({
    mutationFn: variables => fetchPostApiUsersVerify2Fa(_extends({}, fetcherOptions, variables))
  }, options));
};

// https://tanstack.com/query/v5/docs/react/guides/optimistic-updates#via-the-cache
function useDeleteApiAccountOptimistic() {
  const queryClient = useQueryClient();
  const queryKey = queryKeyFn({
    path: '/accounts',
    operationId: 'getApiAccounts',
    variables: {}
  });
  return useDeleteApiAccount({
    onMutate: async variables => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey
      });
      // Snapshot the previous value
      const previousAccounts = queryClient.getQueryData(queryKey);
      // Optimistically update to the new value
      queryClient.setQueryData(queryKey, old => old == null ? void 0 : old.filter(account => `${account.id}` !== variables.pathParams.accountId));
      // Return a context object with the snapshotted value
      return {
        previousAccounts
      };
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(queryKey, context.previousAccounts);
    },
    // Always refetch after error or success:
    onSettled: () => {
      return queryClient.invalidateQueries(queryKey);
    }
  });
}

function usePutApiCalendarOptimistic() {
  const queryClient = useQueryClient();
  const queryKey = queryKeyFn({
    path: '/accounts',
    operationId: 'getApiAccounts',
    variables: {}
  });
  return usePutApiCalendarCalendarsId({
    onMutate: async variables => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey
      });
      // Snapshot the previous value
      const previousAccounts = queryClient.getQueryData(queryKey);
      const calendarId = variables.pathParams.id;
      // Optimistically update to the new value
      queryClient.setQueryData(queryKey, old => {
        // Edge case where we don't have a previous value to update.
        // Without this `if` statement, we would invalidly assign partial state.
        // This avoid errors like "Cannot read property 'id' of undefined".
        if (!old) return old;
        return immer(old, draft => {
          for (const account of draft) {
            if (!account.calendars) continue;
            const calendarIndex = account.calendars.findIndex(calendar => `${calendar.id}` === calendarId);
            if (calendarIndex !== -1) {
              Object.assign(account.calendars[calendarIndex], variables.body);
            }
          }
        });
      });
      // Return a context object with the snapshotted value
      return {
        previousAccounts,
        calendarId
      };
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(queryKey, context.previousAccounts);
    },
    // Always refetch after error or success:
    onSettled: () => {
      return queryClient.invalidateQueries(queryKey);
    }
  });
}

const getAdminTriggersPackagesQueryKey = queryKeyFn({
  path: '/admin/triggers-packages',
  operationId: 'getApiAdminTriggersPackages',
  variables: {}
});
function invalidateAdminCompanyTreeQueryData(queryClient) {
  return Promise.all([queryClient.invalidateQueries(getAdminTriggersPackagesQueryKey), queryClient.invalidateQueries(['admin', 'companies'])]);
}
function updateAdminCompanyTreeQueryData(queryClient, companyTreeSettingsId, updater) {
  const castedId = `${companyTreeSettingsId}`;
  queryClient.setQueryData(['admin', 'companies'], data => {
    if (!data) {
      return data;
    }
    return data.map(company => `${company.id}` === castedId ? updater(company) : company);
  });
  queryClient.setQueryData(queryKeyFn({
    path: '/admin/companies/{id}',
    operationId: 'getApiAdminCompanyByCompanyTreeSettingsId',
    variables: {
      pathParams: {
        id: `${companyTreeSettingsId}`
      }
    }
  }), data => {
    if (!data) {
      return data;
    }
    return updater(data);
  });
}

function useGetCompanyById({
  companyTreeSettingsId
}) {
  const queryClient = useQueryClient();
  return useGetApiAdminCompanyByCompanyTreeSettingsId({
    pathParams: {
      id: `${companyTreeSettingsId}`
    }
  }, {
    // @ts-expect-error react-query v5 + OpenAPI does not support `initialData` yet: https://github.com/fabien0102/openapi-codegen/issues/184
    initialData: () => {
      var _queryClient$getQuery;
      return (_queryClient$getQuery = queryClient.getQueryData(['admin', 'companies'])) == null ? void 0 : _queryClient$getQuery.find(company => company.id === companyTreeSettingsId);
    }
  });
}

function useGetAdminIsAuthenticated(options) {
  return useGetApiAdminIsAuthenticated({}, _extends({
    retry: (failureCount, error) => {
      const status = error == null ? void 0 : error.status;
      if (!status || status >= 400 && status < 500) return false;
      return failureCount < 3;
    }
  }, options));
}

function usePatchAdminCompany(options) {
  const queryClient = useQueryClient();
  return usePatchApiAdminCompany(_extends({}, options, {
    onSuccess: (data, variables, context) => {
      updateAdminCompanyTreeQueryData(queryClient, variables.pathParams.id, company => {
        return _extends({}, company, variables.body);
      });
      options == null || options.onSuccess == null || options.onSuccess(data, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      queryClient.invalidateQueries(['admin', 'companies']);
      options == null || options.onSettled == null || options.onSettled(data, error, variables, context);
    }
  }));
}

function usePatchAdminCompanyEntity(options) {
  const queryClient = useQueryClient();
  return usePatchApiAdminCompanyEntity(_extends({}, options, {
    onSettled: (data, error, variables, context) => {
      queryClient.invalidateQueries(['admin', 'companies']);
      options == null || options.onSettled == null || options.onSettled(data, error, variables, context);
    }
  }));
}

function usePostAdminSyncTriggersPackages(options) {
  const queryClient = useQueryClient();
  return usePostApiAdminSyncTriggersPackages(_extends({}, options, {
    onSuccess: (data, variables, context) => {
      updateAdminCompanyTreeQueryData(queryClient, variables.pathParams.companyTreeSettingsId, cts => _extends({}, cts, {
        triggerPackageVersion: data.triggerPackageVersion
      }));
      options == null || options.onSuccess == null || options.onSuccess(data, variables, context);
    },
    onSettled: async (data, error, variables, context) => {
      await queryClient.invalidateQueries(getAdminTriggersPackagesQueryKey);
      options == null || options.onSettled == null || options.onSettled(data, error, variables, context);
    }
  }));
}

function usePostAdminTriggersPackages(options) {
  const {
    fetcherOptions
  } = useSitContext();
  return useMutation(async variables => {
    const formData = new FormData();
    for (const key of variables.body.companyTreeSettingsIds) {
      formData.append('companyTreeSettingsIds', `${key}`);
    }
    formData.append('fileName', variables.body.fileName);
    return await fetchPostApiAdminTriggersPackages(_extends({}, fetcherOptions, variables, {
      // @ts-expect-error -- overriding the body type to be FormData
      body: formData,
      headers: _extends({}, fetcherOptions.headers, {
        'Content-Type': 'multipart/form-data'
      })
    }));
  }, options);
}

const STALE_TIMES = {
  /** 5 seconds */
  short: 5000,
  /** 1 minute */
  medium: 60000,
  /** 5 minutes */
  long: 300000
};
const CACHE_TIMES = {
  /** 2 mins */
  short: 120000,
  /** 10 minutes */
  medium: 600000,
  /** 30 minutes */
  long: 1800000
};
const PERSIST_META = {
  persist: true
};
function setQueryData(queryClient, queryKey, callback) {
  return queryClient.setQueryData(queryKey, old => {
    if (!old) return old;
    return callback(old);
  });
}
function setQueriesData(queryClient, queryKey, callback) {
  return queryClient.setQueriesData({
    queryKey
  }, old => {
    if (!old) return old;
    return callback(old);
  });
}

function useGetApprovalEntries(variables, options) {
  return useGetApiApprovalsEntries(variables, _extends({
    staleTime: STALE_TIMES.short,
    cacheTime: CACHE_TIMES.short
  }, options));
}

function useGetApprovalTimesheets(variables, options) {
  return useGetApiApprovalsTimesheets(variables, _extends({
    staleTime: STALE_TIMES.short,
    cacheTime: CACHE_TIMES.short
  }, options));
}

// @ts-expect-error -- we are using this for invalidation, so we don't want to query params
const approvalsEntriesQueryKey = queryKeyFn({
  path: '/approvals/entries',
  operationId: 'getApiApprovalsEntries',
  variables: {}
});
// @ts-expect-error -- we are using this for invalidation, so we don't want to query params
const approvalsTimesheetsQueryKey = queryKeyFn({
  path: '/approvals/timesheets',
  operationId: 'getApiApprovalsTimesheets',
  variables: {}
});

function invalidateApprovalsTimesheetsQuery(queryClient) {
  return queryClient.invalidateQueries(approvalsEntriesQueryKey);
}
function invalidateApprovalsEntriesQuery(queryClient) {
  return queryClient.invalidateQueries(approvalsTimesheetsQueryKey);
}

const useInvalidateGetApprovalEntries = () => {
  const client = useQueryClient();
  return useCallback(() => invalidateApprovalsEntriesQuery(client), [client]);
};
const useInvalidateGetApprovalTimesheets = () => {
  const client = useQueryClient();
  return useCallback(() => invalidateApprovalsTimesheetsQuery(client), [client]);
};

const getUserQueryKey = queryKeyFn({
  path: '/users',
  operationId: 'getApiUser',
  variables: {}
});

const invalidateUserQuery = queryClient => queryClient.invalidateQueries(getUserQueryKey);
const getUserQueryData = queryClient => queryClient.getQueryData(getUserQueryKey);
function setUserQueryData(queryClient, updater) {
  queryClient.setQueryData(getUserQueryKey, oldData => produce(oldData, updater));
}

function usePostLogin(options) {
  const queryClient = useQueryClient();
  return usePostApiAuthLogin(_extends({}, options, {
    onSuccess: (data, variables, context) => {
      // `data.user` is the same result as `useGetUser`.
      // Therefore, we can update the `useGetUser` cache here.
      setUserQueryData(queryClient, () => data.user);
      options == null || options.onSuccess == null || options.onSuccess(data, variables, context);
    }
  }));
}

const dimensionPageSize = 100000;

const useGetApiClientsInfinite = (variables, options) => {
  var _variables$queryParam, _variables$queryParam2;
  const {
    fetcherOptions,
    queryKeyFn,
    queryOptions
  } = useSitContext();
  const pageNumber = (_variables$queryParam = (_variables$queryParam2 = variables.queryParams) == null ? void 0 : _variables$queryParam2.pageNumber) != null ? _variables$queryParam : 1;
  const queryParams = _extends({}, variables.queryParams, {
    activeOnly: 'true',
    pageNumber: `${pageNumber}`
  });
  return useInfiniteQuery([...queryKeyFn({
    path: '/clients',
    operationId: 'getApiClients',
    variables: _extends({}, variables, {
      queryParams
    })
  }), pageNumber], ({
    signal,
    pageParam
  }) => fetchGetApiClients(_extends({}, fetcherOptions, variables, {
    queryParams: _extends({}, queryParams, {
      pageNumber: `${pageParam != null ? pageParam : pageNumber}`
    })
  }), signal), _extends({}, options, queryOptions, {
    getNextPageParam: (lastPage, pages) => {
      var _variables$queryParam3;
      const pageSize = (_variables$queryParam3 = variables.queryParams) == null ? void 0 : _variables$queryParam3.pageSize;
      if (lastPage.length < (pageSize ? Number(pageSize) : 20)) {
        return;
      }
      return pages.length + 1;
    }
  }));
};

const _excluded$3 = ["hasNextPage", "fetchNextPage", "isFetchingNextPage", "hasPreviousPage", "fetchPreviousPage", "isFetchingPreviousPage"];
function useGetClients(variables, options) {
  const result = useGetApiClientsInfinite(_extends({}, variables, {
    queryParams: _extends({}, variables.queryParams, {
      pageNumber: '1',
      pageSize: `${dimensionPageSize}`
    })
  }), options);
  const data = useMemo(() => {
    var _result$data;
    return (_result$data = result.data) == null ? void 0 : _result$data.pages.flat();
  }, [result.data]);
  const rest = _objectWithoutPropertiesLoose(result, _excluded$3);
  return _extends({}, rest, {
    data
  });
}

/** Use if you want to invalidate queries across timesheets */
const getClustersQueryKey = ['clusters', 'timesheet'];
function getClusterByTimesheetQueryKey(timesheetId) {
  return queryKeyFn({
    path: '/clusters/timesheet/{timesheetId}',
    operationId: 'getApiClustersForTimesheet',
    variables: {
      pathParams: {
        timesheetId
      }
    }
  });
}
function setClusterByTimesheetQueryData(queryClient, timesheetId, updater) {
  return queryClient.setQueryData(timesheetId ? getClusterByTimesheetQueryKey(timesheetId) : getClustersQueryKey, oldData => oldData ? updater(oldData) : oldData);
}

function getClustersQueriesData(queryClient) {
  return queryClient.getQueriesData(getClustersQueryKey);
}
function setClusterQueryData(queryClient, clusterId, updater) {
  return queryClient.setQueriesData(getClustersQueryKey, produce(draft => {
    if (!draft) return;
    const c = draft.find(c => c.id === clusterId);
    if (c) {
      updater(c);
    }
    return draft;
  }));
}
function deleteClusterNodeQueryData(queryClient, clusterId, nodeId) {
  return setClusterByTimesheetQueryData(queryClient, undefined, oldData => {
    return produce(oldData, draft => {
      var _cluster$nodes;
      const cluster = draft.find(c => c.id === clusterId);
      if (!cluster) return;
      cluster.nodes = (_cluster$nodes = cluster.nodes) == null ? void 0 : _cluster$nodes.filter(n => n.id !== nodeId);
    });
  });
}
function updateClusterNodeQueryData(queryClient, clusterId, nodeId, body) {
  return setClusterByTimesheetQueryData(queryClient, undefined, oldData => {
    return produce(oldData, draft => {
      var _cluster$nodes2;
      const cluster = draft.find(c => c.id === clusterId);
      const node = cluster == null || (_cluster$nodes2 = cluster.nodes) == null ? void 0 : _cluster$nodes2.find(n => n.id === nodeId);
      if (!node) return;
      Object.assign(node, body);
    });
  });
}

function useDeleteClusterNode(options) {
  const queryClient = useQueryClient();
  return useDeleteApiClusterNode(_extends({}, options, {
    onSuccess: (data, variables, context) => {
      deleteClusterNodeQueryData(queryClient, variables.pathParams.id, variables.pathParams.nodeId);
      options == null || options.onSuccess == null || options.onSuccess(data, variables, context);
    }
  }));
}

function useGetClustersByTimesheet(timesheetId, options) {
  return useGetApiClustersForTimesheet({
    // biome-ignore lint/style/noNonNullAssertion: enabled handles this case
    pathParams: {
      timesheetId: timesheetId
    }
  }, _extends({
    cacheTime: CACHE_TIMES.medium,
    staleTime: STALE_TIMES.medium
  }, options, {
    enabled: !!timesheetId && (options == null ? void 0 : options.enabled) !== false
  }));
}

function usePostClusterSplit(timesheetId, options) {
  const queryClient = useQueryClient();
  return usePostApiClusterSplit(_extends({}, options, {
    onSuccess: (data, variables, context) => {
      setClusterByTimesheetQueryData(queryClient, timesheetId, currentData => {
        const newData = currentData.map(cluster => {
          if (cluster.id === variables.pathParams.id && cluster.nodes) {
            return _extends({}, cluster, {
              nodes: cluster.nodes.filter(node => !variables.body.nodesToRemove.includes(node.id))
            });
          }
          return cluster;
        });
        newData.unshift(data);
        return newData;
      });
      options == null || options.onSuccess == null || options.onSuccess(data, variables, context);
    }
  }));
}

function usePutCluster(options) {
  const queryClient = useQueryClient();
  const result = usePutApiCluster(_extends({}, options, {
    onMutate: variables => {
      // Snapshot the current state
      const previousData = getClustersQueriesData(queryClient);
      setClusterQueryData(queryClient, variables.pathParams.id, draft => Object.assign(draft, variables.body));
      options == null || options.onMutate == null || options.onMutate(variables);
      // Return the previous data to use for rollback
      return {
        previousData
      };
    },
    onSuccess: (data, variables, context) => {
      // Update the query data with the response data
      setClusterQueryData(queryClient, data.id, draft => Object.assign(draft, data));
      // Call the original onSuccess callback if provided
      options == null || options.onSuccess == null || options.onSuccess(data, variables, context);
    },
    onError: (error, variables, context) => {
      const ctx = context;
      // Rollback to the previous data on error
      if (ctx != null && ctx.previousData) {
        for (const [key, data] of ctx.previousData) {
          queryClient.setQueryData(key, data);
        }
      }
      // Call the original onError callback if provided
      options == null || options.onError == null || options.onError(error, variables, context);
    }
  }));
  return result;
}

function usePutClusterNode(options) {
  const queryClient = useQueryClient();
  return usePutApiClusterNode(_extends({}, options, {
    onMutate: variables => {
      options == null || options.onMutate == null || options.onMutate(variables);
    },
    onSuccess: (data, variables, context) => {
      options == null || options.onSuccess == null || options.onSuccess(data, variables, context);
      updateClusterNodeQueryData(queryClient, variables.pathParams.id, variables.pathParams.nodeId, data);
    },
    onSettled: async (data, error, variables, context) => {
      options == null || options.onSettled == null || options.onSettled(data, error, variables, context);
      // Since we updated the cluster node overlaps, we should invalidate the cluster query
      await queryClient.invalidateQueries(getClustersQueryKey);
    }
  }));
}

function useGetCompany(variables, options) {
  return useGetApiCompany(variables, _extends({
    staleTime: 30 * 1000,
    // 30 seconds
    cacheTime: 60 * 60 * 1000
  }, options));
}

function useGetUser(options) {
  return useGetApiUser({}, _extends({
    staleTime: STALE_TIMES.medium,
    // We want to cache the user data indefinitely so that an authenticated user is always authenticated
    // unless explicitly logged out.
    cacheTime: Number.POSITIVE_INFINITY
  }, options, {
    meta: PERSIST_META
  }));
}

function useIntacctCompanyId(options) {
  var _useGetUser$data;
  return (_useGetUser$data = useGetUser(options).data) == null ? void 0 : _useGetUser$data.company.intacctCompanyId;
}

function usePutCompany(options) {
  const queryClient = useQueryClient();
  const getQueryKey = companyId => queryKeyFn({
    path: '/companies',
    operationId: 'getApiCompany',
    variables: {
      queryParams: {
        companyId
      }
    }
  });
  return usePutApiCompaniesByUuid(_extends({}, options, {
    onMutate: async variables => {
      const queryKey = getQueryKey(variables.pathParams.companyId);
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey
      });
      // Snapshot the previous value
      const previousCompany = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, old => {
        // Edge case where we don't have a previous value to update.
        // Without this `if` statement, we would invalidly assign partial state.
        // This avoid errors like "Cannot read property 'id' of undefined".
        if (!old) return old;
        return immer(old, draft => {
          Object.assign(draft, variables.body);
        });
      });
      // Return a context object with the snapshotted value
      return {
        previousCompany
      };
    },
    onSuccess: async (data, variables, context) => {
      await (options == null || options.onSuccess == null ? void 0 : options.onSuccess(data, variables, context));
      queryClient.setQueryData(getQueryKey(variables.pathParams.companyId), data);
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: async (error, variables, context) => {
      await (options == null || options.onError == null ? void 0 : options.onError(error, variables, context));
      queryClient.setQueryData(getQueryKey(variables.pathParams.companyId), context.previousCompany);
      await queryClient.invalidateQueries(getQueryKey(variables.pathParams.companyId));
    }
  }));
}

const dataStatusQueryKey = queryKeyFn({
  path: '/data-status',
  operationId: 'getApiDataStatus',
  variables: {}
});

const invalidateDataStatusQuery = queryClient => queryClient.invalidateQueries(dataStatusQueryKey);

const useInvalidateDataStatus = () => {
  const client = useQueryClient();
  return useCallback(() => invalidateDataStatusQuery(client), [client]);
};

/*
 * 1:   [Optional] On load of the **timesheets** page, /api/dimensions?companyId=current-sit-company-uuid
 *      This populates the cache with the dimensions for the company, the "most common" scenario,
 *      resulting in perf improvements.
 * 2.:  On **timesheet** page:
 * 2.1: If you have the companyId associated with the timesheet (stored in Redux, cache, or whatever), use the call /api/dimensions?companyId=SIT-COMPANY-ID
 *      Update the cache with the dimensions for the company: Record<SITCompanyId, DimensionsData>
 * 2.2: If you DON’T have the companyId associated with the timesheet (deep link, page reload), use the call /api/dimensions?timesheetId=SIT-TIMESHEET-ID
 *      Update the cache with the dimensions for the company: Record<SITCompanyId, DimensionsData>
 */
function useGetDimensions({
  companyId,
  timesheetId,
  projectId,
  taskId
}, options) {
  //   const queryClient = useQueryClient();
  // If passed a `companyId`, we can use it immediately.
  // If passed a `timesheetId`, we first try to get the `companyId` from the cache.
  // If we don't find it in the cache, we DO NOT make a request for it.
  // Instead, we use the `timesheetId` as-is.
  const qp = {
    activeOnly: 'true'
  };
  let enabled = true;
  if (companyId) {
    qp.companyId = companyId;
  } else if (timesheetId) {
    // TODO: Here, we should be inspecting the cache to see if we can map the `timesheetId` to a `companyId`.
    qp.timesheetId = timesheetId;
  } else {
    enabled = false;
  }
  if (projectId) {
    qp.projectId = projectId;
  }
  if (taskId) {
    qp.taskId = taskId;
  }
  return useGetApiDimensions({
    queryParams: qp
  }, _extends({
    staleTime: 1000 * 60 * 5
  }, options, {
    enabled: enabled && (options == null ? void 0 : options.enabled) !== false
  }));
}

function getEmployeesQueryData(queryClient) {
  return queryClient.getQueriesData(queryKeyFn({
    path: '/employees',
    operationId: 'getApiEmployees',
    variables: {}
  }));
}

function useValidateInvite(inviteToken, variables, options) {
  return useGetApiPubInvitesInviteToken(_extends({
    pathParams: {
      // biome-ignore lint/style/noNonNullAssertion: the enabled flag ensures this is defined
      inviteToken: inviteToken
    }
  }, variables), _extends({
    retry: (failureCount, error) => {
      const status = error == null ? void 0 : error.status;
      if (!status || status >= 400 && status < 500) return false;
      return failureCount < 3;
    },
    cacheTime: 0,
    staleTime: 0
  }, options, {
    enabled: !!inviteToken && (options == null ? void 0 : options.enabled) !== false
    // TODO: we can consider setting the `getUser` query cache value to the value of the result of this query
  }));
}

const useGetApiItemsInfinite = (variables, options) => {
  var _variables$queryParam, _variables$queryParam2;
  const {
    fetcherOptions,
    queryKeyFn,
    queryOptions
  } = useSitContext();
  const pageNumber = (_variables$queryParam = (_variables$queryParam2 = variables.queryParams) == null ? void 0 : _variables$queryParam2.pageNumber) != null ? _variables$queryParam : 1;
  const queryParams = _extends({}, variables.queryParams, {
    activeOnly: 'true',
    pageNumber: `${pageNumber}`
  });
  return useInfiniteQuery([...queryKeyFn({
    path: '/items',
    operationId: 'getApiItems',
    variables: _extends({}, variables, {
      queryParams
    })
  }), pageNumber], ({
    signal,
    pageParam
  }) => fetchGetApiItems(_extends({}, fetcherOptions, variables, {
    queryParams: _extends({}, queryParams, {
      pageNumber: `${pageParam != null ? pageParam : pageNumber}`
    })
  }), signal), _extends({
    cacheTime: CACHE_TIMES.long,
    staleTime: STALE_TIMES.long
  }, options, queryOptions, {
    getNextPageParam: (lastPage, pages) => {
      var _variables$queryParam3;
      const pageSize = (_variables$queryParam3 = variables.queryParams) == null ? void 0 : _variables$queryParam3.pageSize;
      if (lastPage.length < (pageSize ? Number(pageSize) : 20)) {
        return;
      }
      return pages.length + 1;
    }
  }));
};

function useGetItems(variables, options) {
  const result = useGetApiItemsInfinite(_extends({}, variables, {
    queryParams: _extends({}, variables.queryParams, {
      pageNumber: '1',
      pageSize: `${dimensionPageSize}`
    })
  }), options);
  const data = useMemo(() => {
    var _result$data;
    return (_result$data = result.data) == null ? void 0 : _result$data.pages.flat();
  }, [result.data]);
  return _extends({}, result, {
    data
  });
}

function sortNotificationsDesc(notifications) {
  return notifications.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
}

function useGetNotifications(variables, options) {
  return useGetApiNotifications(variables, _extends({
    staleTime: STALE_TIMES.short,
    cacheTime: CACHE_TIMES.medium
  }, options, {
    select: sortNotificationsDesc
  }));
}

const getNotificationQueryKey = queryKeyFn({
  path: '/notifications',
  operationId: 'getApiNotifications',
  variables: {}
});

function usePostNotificationsReadAll(options) {
  const queryClient = useQueryClient();
  return usePostApiNotificationsReadAll(_extends({}, options, {
    onMutate: async variables => {
      await queryClient.cancelQueries(getNotificationQueryKey);
      // Snapshot the previous value
      const queryData = queryClient.getQueryData(getNotificationQueryKey);
      queryClient.setQueryData(getNotificationQueryKey, queryData == null ? void 0 : queryData.map(notification => _extends({}, notification, {
        read: true
      })));
      options == null || options.onMutate == null || options.onMutate(variables);
      return {
        queryData
      };
    },
    onError: (error, variables, context) => {
      queryClient.setQueryData(getNotificationQueryKey, context.queryData);
      options == null || options.onError == null || options.onError(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      queryClient.setQueryData(getNotificationQueryKey, sortNotificationsDesc(data));
      options == null || options.onSuccess == null || options.onSuccess(data, variables, context);
    }
  }));
}

const useGetApiProjectsInfinite = (variables, options) => {
  var _variables$queryParam, _variables$queryParam2;
  const {
    fetcherOptions,
    queryKeyFn,
    queryOptions
  } = useSitContext();
  const pageNumber = (_variables$queryParam = (_variables$queryParam2 = variables.queryParams) == null ? void 0 : _variables$queryParam2.pageNumber) != null ? _variables$queryParam : 1;
  const queryParams = _extends({}, variables.queryParams, {
    activeOnly: 'true',
    pageNumber: `${pageNumber}`
  });
  return useInfiniteQuery([...queryKeyFn({
    path: '/projects',
    operationId: 'getApiProjects',
    variables: _extends({}, variables, {
      queryParams
    })
  }), pageNumber], ({
    signal,
    pageParam
  }) => fetchGetApiProjects(_extends({}, fetcherOptions, variables, {
    queryParams: _extends({}, queryParams, {
      pageNumber: `${pageParam != null ? pageParam : pageNumber}`
    })
  }), signal), _extends({}, options, queryOptions, {
    getNextPageParam: (lastPage, pages) => {
      var _variables$queryParam3;
      const pageSize = (_variables$queryParam3 = variables.queryParams) == null ? void 0 : _variables$queryParam3.pageSize;
      if (lastPage.length < (pageSize ? Number(pageSize) : 20)) {
        return;
      }
      return pages.length + 1;
    }
  }));
};

const _excluded$2 = ["hasNextPage", "fetchNextPage", "isFetchingNextPage", "hasPreviousPage", "fetchPreviousPage", "isFetchingPreviousPage"];
function useGetProjects(variables, options) {
  const result = useGetApiProjectsInfinite(_extends({}, variables, {
    queryParams: _extends({}, variables.queryParams, {
      pageNumber: '1',
      pageSize: `${dimensionPageSize}`
    })
  }), options);
  const data = useMemo(() => {
    var _result$data;
    return (_result$data = result.data) == null ? void 0 : _result$data.pages.flat();
  }, [result.data]);
  const rest = _objectWithoutPropertiesLoose(result, _excluded$2);
  return _extends({}, rest, {
    data
  });
}

function usePostEmployeeSignup(options) {
  const queryClient = useQueryClient();
  return usePostApiPubEmployeeSignup(_extends({}, options, {
    onSuccess: (data, variables, context) => {
      // We have logged in, so update the user state.
      setUserQueryData(queryClient, () => data);
      options == null || options.onSuccess == null || options.onSuccess(data, variables, context);
    }
  }));
}

const useGetApiTasksInfinite = (variables, options) => {
  var _variables$queryParam, _variables$queryParam2;
  const {
    fetcherOptions,
    queryKeyFn,
    queryOptions
  } = useSitContext();
  const pageNumber = (_variables$queryParam = (_variables$queryParam2 = variables.queryParams) == null ? void 0 : _variables$queryParam2.pageNumber) != null ? _variables$queryParam : 1;
  const queryParams = _extends({}, variables.queryParams, {
    activeOnly: 'true',
    pageNumber: `${pageNumber}`
  });
  return useInfiniteQuery([...queryKeyFn({
    path: '/tasks',
    operationId: 'getApiTasks',
    variables: _extends({}, variables, {
      queryParams
    })
  }), pageNumber], ({
    signal,
    pageParam
  }) => fetchGetApiTasks(_extends({}, fetcherOptions, variables, {
    queryParams: _extends({}, queryParams, {
      pageNumber: pageParam != null ? pageParam : pageNumber
    })
  }), signal), _extends({
    cacheTime: CACHE_TIMES.long,
    staleTime: STALE_TIMES.long
  }, options, queryOptions, {
    getNextPageParam: (lastPage, pages) => {
      var _variables$queryParam3;
      const pageSize = (_variables$queryParam3 = variables.queryParams) == null ? void 0 : _variables$queryParam3.pageSize;
      if (lastPage.length < (pageSize ? Number(pageSize) : 20)) {
        return;
      }
      return pages.length + 1;
    }
  }));
};

const _excluded$1 = ["hasNextPage", "fetchNextPage", "isFetchingNextPage", "hasPreviousPage", "fetchPreviousPage", "isFetchingPreviousPage"];
function useGetTasks(variables, options) {
  const result = useGetApiTasksInfinite(_extends({}, variables, {
    queryParams: _extends({}, variables.queryParams, {
      pageNumber: '1',
      pageSize: `${dimensionPageSize}`
    })
  }), options);
  const data = useMemo(() => {
    var _result$data;
    return (_result$data = result.data) == null ? void 0 : _result$data.pages.flat();
  }, [result.data]);
  const rest = _objectWithoutPropertiesLoose(result, _excluded$1);
  return _extends({}, rest, {
    data
  });
}

function useGetTeam(options) {
  return useGetApiTeam({}, _extends({
    cacheTime: CACHE_TIMES.medium,
    staleTime: STALE_TIMES.medium
  }, options));
}

const useGetApiTimeTypesInfinite = (variables, options) => {
  var _variables$queryParam, _variables$queryParam2, _variables$queryParam3;
  const {
    fetcherOptions,
    queryKeyFn,
    queryOptions
  } = useSitContext();
  const enabled = !!((_variables$queryParam = variables.queryParams) != null && _variables$queryParam.timesheetId) && (options == null ? void 0 : options.enabled) !== false;
  const pageNumber = (_variables$queryParam2 = (_variables$queryParam3 = variables.queryParams) == null ? void 0 : _variables$queryParam3.pageNumber) != null ? _variables$queryParam2 : 1;
  const queryParams = _extends({}, variables.queryParams, {
    activeOnly: 'true',
    pageNumber: `${pageNumber}`
  });
  return useInfiniteQuery([...queryKeyFn({
    path: '/time-types',
    operationId: 'getApiTimeTypes',
    variables: _extends({}, variables, {
      queryParams
    })
  }), pageNumber], ({
    signal,
    pageParam
  }) => fetchGetApiTimeTypes(_extends({}, fetcherOptions, variables, {
    queryParams: _extends({}, queryParams, {
      pageNumber: pageParam != null ? pageParam : pageNumber
    })
  }), signal), _extends({
    staleTime: 1000 * 60 * 5,
    // 5 minutes
    cacheTime: 1000 * 60 * 60 * 12
  }, options, queryOptions, {
    enabled,
    getNextPageParam: (lastPage, pages) => {
      var _variables$queryParam4, _variables$queryParam5;
      if (lastPage.length < ((_variables$queryParam4 = variables.queryParams) != null && _variables$queryParam4.pageSize ? Number((_variables$queryParam5 = variables.queryParams) == null ? void 0 : _variables$queryParam5.pageSize) : 20)) {
        return;
      }
      return pages.length + 1;
    }
  }));
};

/**
 * Unpaginaged time types.
 * @param timesheetId
 * @param options
 * @returns
 */
function useGetTimeTypes(timesheetId, options) {
  const enabled = !!timesheetId && (options == null ? void 0 : options.enabled) !== false;
  return useGetApiTimeTypes({
    queryParams: {
      timesheetId,
      activeOnly: 'true'
    }
  }, _extends({
    staleTime: 1000 * 60 * 5,
    // 5 minutes
    cacheTime: 1000 * 60 * 60 * 12
  }, options, {
    enabled
  }));
}

function calculateHours(entries) {
  const partial = entries.reduce((timesheetHours, entry) => {
    if (entry.duration === null) {
      return timesheetHours;
    }
    const duration = Number.parseInt(`${entry.duration}`, 10);
    if (entry.billable) {
      timesheetHours.totalBillable += duration;
    } else {
      timesheetHours.totalNonBillable += duration;
    }
    return timesheetHours;
  }, {
    totalBillable: 0,
    totalNonBillable: 0
  });
  return _extends({}, partial, {
    totalHours: partial.totalBillable + partial.totalNonBillable
  });
}

function useGetTimeEntries(timesheetId, options) {
  const result = useGetApiTimesheetTimeEntries({
    pathParams: {
      // biome-ignore lint/style/noNonNullAssertion: enabled
      id: timesheetId
    }
  }, _extends({
    staleTime: 1000 * 60 * 2,
    // 2 minutes
    cacheTime: 1000 * 60 * 20
  }, options, {
    enabled: !!timesheetId && (options == null ? void 0 : options.enabled) !== false
  }));
  const data = useMemo(() => {
    if (result.data) {
      const entries = {};
      for (const [entryId, entry] of Object.entries(result.data.entries)) {
        const timesheetRowId = entry.timesheetRowId;
        const group = result.data.entryGroups.groups[timesheetRowId];
        entries[entryId] = _extends({}, entry, {
          billable: group.billable,
          dimensionsValues: _extends({}, group.dimensionValues, entry.dimensionsValues)
        });
      }
      return entries;
    }
  }, [result.data]);
  return _extends({}, result, {
    data
  });
}

function useGetTimesheetEntries(variables, options) {
  return useGetApiTimesheetsByUuidEntries(variables, options);
}

function useGetTimesheetEntry(timesheetId, entryId, variables, options) {
  return useGetApiTimesheetsEntryById(_extends({
    pathParams: {
      entryId,
      id: timesheetId
    }
  }, variables), _extends({
    cacheTime: CACHE_TIMES.short,
    staleTime: STALE_TIMES.short
  }, options, {
    enabled: !!timesheetId && !!entryId && (options == null ? void 0 : options.enabled) !== false
  }));
}

/**
 * Compared to `useGetTimesheetEntry`, this function avoid a network call by using already
 * fetched data from `useGetApiTimesheetTimeEntries`.
 */
function useGetTimesheetEntryFromEntries(timesheetId, timesheetEntryId, options) {
  const result = useGetApiTimesheetTimeEntries({
    pathParams: {
      id: timesheetId
    }
  }, _extends({
    staleTime: 1000 * 60 * 2,
    // 2 minutes
    cacheTime: 1000 * 60 * 20
  }, options, {
    enabled: !!timesheetId && (options == null ? void 0 : options.enabled) !== false
  }));
  const data = useMemo(() => {
    if (result.data) {
      const timesheetRowId = result.data.entries[timesheetEntryId].timesheetRowId;
      const group = result.data.entryGroups.groups[timesheetRowId];
      return _extends({}, result.data.entries[timesheetEntryId], {
        billable: group.billable,
        dimensionsValues: _extends({}, group.dimensionValues, result.data.entries[timesheetEntryId].dimensionsValues)
      });
    }
  }, [result.data, timesheetEntryId]);
  return _extends({}, result, {
    data
  });
}

const getTimeEntriesQueryKey = timesheetId => queryKeyFn({
  path: '/timesheets/{id}/time-entries',
  operationId: 'getApiTimesheetTimeEntries',
  variables: {
    pathParams: {
      id: timesheetId
    }
  }
});
const getTimesheetEntriesQueryKey = timesheetId => queryKeyFn({
  path: '/timesheets/{id}/entries',
  operationId: 'getApiTimesheetsByUuidEntries',
  variables: {
    pathParams: {
      id: timesheetId
    }
  }
});
const getTimesheetEntryQueryKey = (timesheetId, entryId) => queryKeyFn({
  path: '/timesheets/{id}/entries/{entryId}',
  operationId: 'getApiTimesheetsEntryById',
  variables: {
    pathParams: {
      id: timesheetId,
      entryId
    }
  }
});

/**
 * @param timesheetEntryId optimistic `timesheetEntryId`
 * @param options
 * @returns
 */
function usePostTimesheetEntries(timesheetEntryId, options) {
  const queryClient = useQueryClient();
  return usePostApiTimesheetsByUuidEntries(_extends({}, options, {
    onMutate: async variables => {
      // Strictly define the `id` property
      variables.body.id = timesheetEntryId;
      const {
        id
      } = variables.pathParams;
      const queryKey = getTimeEntriesQueryKey(id);
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(queryKey);
      // Snapshot the previous value
      const previousEntries = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, old => {
        // Edge case where we don't have a previous value to update.
        // Without this `if` statement, we would invalidly assign partial state.
        // This avoid errors like "Cannot read property 'id' of undefined".
        if (!old) return old;
        return immer(old, draft => {
          const {
            billable = false,
            confirmedClientId: clientId = null,
            confirmedProjectId: projectId = null,
            confirmedItemId: itemId = null,
            confirmedTaskId: taskId = null,
            timeTypeId = null,
            date,
            description = null,
            duration,
            timesheetRowId
          } = variables.body;
          // @ts-expect-error -- partial assignment is technically invalid.
          old.entries[timesheetEntryId] = {
            id: timesheetEntryId,
            date,
            description,
            duration
          };
          if (timesheetRowId) {
            var _draft$entryGroups$gr, _draft$entryGroups$gr2;
            (_draft$entryGroups$gr2 = (_draft$entryGroups$gr = draft.entryGroups.groups)[timesheetRowId]) != null ? _draft$entryGroups$gr2 : _draft$entryGroups$gr[timesheetRowId] = {
              id: timesheetRowId,
              billable,
              entryIds: [],
              dimensionValues: {
                clientId,
                projectId,
                itemId,
                taskId,
                timeTypeId,
                departmentId: null,
                locationId: null
              }
            };
            draft.entryGroups.groups[timesheetRowId].entryIds = [...draft.entryGroups.groups[timesheetRowId].entryIds, timesheetEntryId];
            draft.entryGroups.ids = Array.from(new Set([...draft.entryGroups.ids, timesheetRowId]));
          }
        });
      });
      return {
        previousEntries
      };
    },
    onError: async (_error, variables, context) => {
      const ctx = context;
      const queryKey = getTimeEntriesQueryKey(variables.pathParams.id);
      queryClient.setQueryData(queryKey, ctx.previousEntries);
      await (options == null || options.onError == null ? void 0 : options.onError(_error, variables, context));
    },
    onSuccess: async (data, variables, context) => {
      const queryKey = getTimeEntriesQueryKey(variables.pathParams.id);
      queryClient.setQueryData(queryKey, old => {
        if (!old) return old;
        const {
          timesheetRowId
        } = data;
        return immer(old, draft => {
          var _data$duration, _draft$entryGroups$gr3, _draft$entryGroups$gr4;
          draft.entries[data.id] = {
            approverNotes: data.approverNotes,
            approverUserId: data.approver_user_id,
            date: data.date,
            description: data.description,
            duration: (_data$duration = data.duration) != null ? _data$duration : 0,
            // This is not possible to accomplish populating due to limitations with the current API.
            // We should consider refactoring the API to support this
            // by providing the full dimensionValues object instead of just the IDs.
            // Until then, we have to do a
            dimensionsValues: {},
            id: data.id,
            integrationError: data.integrationError,
            integrationSyncTime: data.integrationSyncTime,
            notes: data.notes,
            status: data.status,
            timesheetRowId
          };
          draft.entryGroups.ids = Array.from(new Set([...draft.entryGroups.ids, timesheetRowId]));
          draft.entryGroups.groups[timesheetRowId].entryIds = Array.from(new Set([...draft.entryGroups.groups[timesheetRowId].entryIds, data.id]));
          (_draft$entryGroups$gr4 = (_draft$entryGroups$gr3 = draft.entryGroups.groups)[timesheetRowId]) != null ? _draft$entryGroups$gr4 : _draft$entryGroups$gr3[timesheetRowId] = {
            id: timesheetRowId,
            billable: data.billable,
            entryIds: [],
            dimensionValues: {
              clientId: data.confirmedClientId,
              projectId: data.confirmedProjectId,
              itemId: data.confirmedItemId,
              taskId: data.confirmedTaskId,
              timeTypeId: data.timeTypeId,
              // NOT SURE HERE
              departmentId: null,
              locationId: null
            }
          };
        });
      });
      await (options == null || options.onSuccess == null ? void 0 : options.onSuccess(data, variables, context));
    },
    onSettled: async (_data, _error, variables, _context) => {
      const queryKey = getTimeEntriesQueryKey(variables.pathParams.id);
      // This is only currently necessary because the return of the `onMutate` function
      // does not match the structure of the cache context type.
      // This will be fixed in the future when we adopt the GET/POST/PUT/DELETE routes that all match the same structure.
      // In other words, the POST/PUT routes will return the same data structure as the GET, allowing for the cache to be updated in a consistent manner.
      await queryClient.invalidateQueries(queryKey);
      await (options == null || options.onSettled == null ? void 0 : options.onSettled(_data, _error, variables, _context));
    }
  }));
}

/**
 * Avoids having to rewrite the repetitive:
 * ```ts
 * (old) => produce(old, (draft) => callback(draft)),
 * ```
 * @param callback a function that takes a draft and modifies it
 */
const produceCallback = callback => old => produce(old, draft => callback(draft));

/// Time entries are the newer APIs. They are currently unused.
function getTimeEntriesQueryData(queryClient, id) {
  return queryClient.getQueryData(getTimeEntriesQueryKey(id));
}
const setTimeEntriesQueryData = (queryClient, id, callback) => setQueryData(queryClient, getTimeEntriesQueryKey(id), produceCallback(callback));
const getTimesheetEntriesQueryData = (queryClient, id) => queryClient.getQueryData(getTimesheetEntriesQueryKey(id));
const setTimesheetEntriesQueryData = (queryClient, id, callback) => setQueryData(queryClient, getTimesheetEntriesQueryKey(id), produceCallback(callback));
function getTimesheetEntryQueryData(queryClient, timesheetId, entryId) {
  return queryClient.getQueryData(getTimesheetEntryQueryKey(timesheetId, entryId));
}
const setTimesheetEntryQueryData = (queryClient, timesheetId, entryId, callback) => {
  setQueryData(queryClient, getTimesheetEntryQueryKey(timesheetId, entryId), produceCallback(callback));
  setTimesheetEntriesQueryData(queryClient, timesheetId, draft => {
    for (const entry of draft) {
      if (entry.id === entryId) {
        const updated = callback(entry);
        Object.assign(entry, updated);
        break;
      }
    }
    return draft;
  });
};

function usePutTimesheetEntry(options) {
  const queryClient = useQueryClient();
  return usePutApiTimesheetsByUuidEntriesByEntryId(_extends({}, options, {
    onMutate: async variables => {
      options == null || options.onMutate == null || options.onMutate(variables);
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      const tsEntryQk = getTimesheetEntryQueryKey(variables.pathParams.id, variables.pathParams.entryId);
      //   const timeEntriesQk = getTimeEntriesQueryKey(variables.pathParams.id);
      await queryClient.cancelQueries({
        queryKey: tsEntryQk
      });
      // Snapshot the previous value
      const timesheetEntryData = getTimesheetEntryQueryData(queryClient, variables.pathParams.id, variables.pathParams.entryId);
      setTimesheetEntryQueryData(queryClient, variables.pathParams.id, variables.pathParams.entryId, draft => {
        Object.assign(draft, variables.body);
        return draft;
      });
      // TODO: Update timeEntryCache using updateTimeEntryCache
      return {
        // timeEntriesData,
        timesheetEntryData
      };
    },
    onError: (error, variables, context) => {
      const tsEntryQk = getTimesheetEntryQueryKey(variables.pathParams.id, variables.pathParams.entryId);
      queryClient.setQueryData(tsEntryQk, context.timesheetEntryData);
      options == null || options.onError == null || options.onError(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      setTimesheetEntryQueryData(queryClient, variables.pathParams.id, variables.pathParams.entryId, draft => {
        Object.assign(draft, data);
        return draft;
      });
      options == null || options.onSuccess == null || options.onSuccess(data, variables, context);
    }
  }));
}

const timesheetRowsQueryKey = id => queryKeyFn({
  path: '/timesheets/{id}/rows',
  operationId: 'getApiTimesheetsByUuidRows',
  variables: {
    pathParams: {
      id
    }
  }
});

function invalidateTimesheetRowsQuery(queryClient, id) {
  return queryClient.invalidateQueries(timesheetRowsQueryKey(id));
}
const setTimesheetRowsQueryData = (queryClient, id, callback) => setQueryData(queryClient, timesheetRowsQueryKey(id), produceCallback(callback));

function useDeleteTimesheetRow(options) {
  const queryClient = useQueryClient();
  return useDeleteApiTimesheetsByUuidRows(_extends({}, options, {
    onMutate: async variables => {
      const {
        id: timesheetId
      } = variables.pathParams;
      const timeEntriesQueryKey = getTimeEntriesQueryKey(timesheetId);
      const timesheetEntriesQueryKey = getTimesheetEntriesQueryKey(timesheetId);
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(timeEntriesQueryKey);
      await queryClient.cancelQueries(timesheetEntriesQueryKey);
      // Snapshot the previous value
      const timeEntriesQueryData = getTimeEntriesQueryData(queryClient, timesheetId);
      const timesheetEntriesQueryData = getTimesheetEntriesQueryData(queryClient, timesheetId);
      // Optimistically update to the new value
      setTimeEntriesQueryData(queryClient, timesheetId, draft => {
        var _draft$entryGroups$gr;
        const entryIds = (_draft$entryGroups$gr = draft.entryGroups.groups[variables.pathParams.timesheetRowId]) == null ? void 0 : _draft$entryGroups$gr.entryIds;
        delete draft.entryGroups.groups[variables.pathParams.timesheetRowId];
        for (const entryId of entryIds || []) {
          delete draft.entries[entryId];
        }
        return draft;
      });
      setTimesheetEntriesQueryData(queryClient, timesheetId, draft => draft.filter(entry => entry.timesheetRowId !== variables.pathParams.timesheetRowId));
      options == null || options.onMutate == null || options.onMutate(variables);
      // Return a context object with the snapshotted value
      return {
        timeEntriesQueryData,
        timesheetEntriesQueryData
      };
    },
    onSettled: async (data, error, variables, context) => {
      await Promise.all([queryClient.invalidateQueries(getTimesheetEntriesQueryKey(variables.pathParams.id)), queryClient.invalidateQueries(getTimeEntriesQueryKey(variables.pathParams.id)), queryClient.invalidateQueries(getClusterByTimesheetQueryKey(variables.pathParams.id)), invalidateTimesheetRowsQuery(queryClient, variables.pathParams.id)]);
      options == null || options.onSettled == null || options.onSettled(data, error, variables, context);
    }
  }));
}

function useGetTimesheetRows(variables, options) {
  return useGetApiTimesheetsByUuidRows(_extends({}, variables, {
    pathParams: {
      id: variables.pathParams.id
    }
  }), _extends({
    staleTime: STALE_TIMES.short,
    cacheTime: CACHE_TIMES.short
  }, options, {
    enabled: !!variables.pathParams.id && (options == null ? void 0 : options.enabled) !== false
  }));
}

function usePostTimesheetRow(options) {
  return usePostApiTimesheetsByUuidRows(options);
}

const useInvalidateGetTimesheetRows = () => {
  const client = useQueryClient();
  return useCallback(id => invalidateTimesheetRowsQuery(client, id), [client]);
};

function usePutTimesheetRow(options) {
  const invalidateRows = useInvalidateGetTimesheetRows();
  return usePutApiTimesheetsByUuidRows(_extends({}, options, {
    onSettled: (data, error, variables, context) => {
      invalidateRows(variables.pathParams.id);
      options == null || options.onSettled == null || options.onSettled(data, error, variables, context);
    }
  }));
}

// https://tanstack.com/query/v5/docs/react/guides/optimistic-updates#via-the-cache
function usePutTimesheetRowBillable(options) {
  const queryClient = useQueryClient();
  return usePutApiTimesheetsByUuidRowsBillable(_extends({}, options, {
    onMutate: async variables => {
      const {
        id: timesheetId,
        timesheetRowId
      } = variables.pathParams;
      const rowsQueryKey = timesheetRowsQueryKey(timesheetId);
      const entriesQueryKey = getTimesheetEntriesQueryKey(timesheetId);
      // const timesheetEntriesQueryKey = getTimeEntriesQueryKey(timesheetId);
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await Promise.all([queryClient.cancelQueries(rowsQueryKey), queryClient.cancelQueries(entriesQueryKey)]);
      // Snapshot the previous value
      const rowsQueryData = queryClient.getQueryData(rowsQueryKey);
      const entriesQueryData = queryClient.getQueryData(entriesQueryKey);
      // Optimistically update to the new value
      // setTimeEntriesQueryData(queryClient, timesheetId, (old) => {
      //   if (old.entryGroups.groups[timesheetRowId])
      //     old.entryGroups.groups[timesheetRowId].billable =
      //       variables.body.billable;
      //   return old;
      // });
      setTimesheetRowsQueryData(queryClient, timesheetId, draft => {
        for (const row of draft) {
          if (row.id === timesheetRowId) {
            if (row.dimensionValues) {
              row.dimensionValues.billable = variables.body.billable;
            }
            break;
          }
        }
        return draft;
      });
      setTimesheetEntriesQueryData(queryClient, timesheetId, draft => {
        for (const entry of draft) {
          if (entry.timesheet_id === timesheetRowId) {
            entry.billable = variables.body.billable;
          }
        }
        return draft;
      });
      // Return a context object with the snapshotted value
      return {
        rowsQueryData,
        entriesQueryData
      };
    },
    onSuccess: (data, variables, context) => {
      const {
        id: timesheetId
      } = variables.pathParams;
      setTimesheetRowsQueryData(queryClient, timesheetId, draft => {
        for (const row of draft) {
          if (row.id === variables.pathParams.timesheetRowId) {
            Object.assign(row, data);
            break;
          }
        }
        return draft;
      });
      setTimesheetEntriesQueryData(queryClient, timesheetId, draft => {
        for (const entry of draft) {
          if (entry.timesheetRowId === variables.pathParams.timesheetRowId) {
            Object.assign(entry, data);
            break;
          }
        }
        return draft;
      });
      options == null || options.onSuccess == null || options.onSuccess(data, variables, context);
    },
    onError: (error, variables, context) => {
      const {
        rowsQueryData,
        entriesQueryData
      } = context;
      if (rowsQueryData) {
        queryClient.setQueryData(timesheetRowsQueryKey(variables.pathParams.id), rowsQueryData);
      }
      if (entriesQueryData) {
        queryClient.setQueryData(getTimesheetEntriesQueryKey(variables.pathParams.id), entriesQueryData);
      }
      options == null || options.onError == null || options.onError(error, variables, context);
    }
  }));
}

function usePutTimesheetRowDimensionValues(options) {
  const queryClient = useQueryClient();
  return usePutApiTimesheetsByUuidRowsDimensionValues(_extends({}, options, {
    onSuccess: (data, variables, context) => {
      setTimesheetRowsQueryData(queryClient, variables.pathParams.id, draft => {
        for (const row of draft) {
          if (row.id === variables.pathParams.id) {
            Object.assign(row, data);
            break;
          }
        }
        return draft;
      });
      options == null || options.onSuccess == null || options.onSuccess(data, variables, context);
    }
  }));
}

const timesheetsQueryKey = queryKeyFn({
  path: '/timesheets',
  operationId: 'getApiTimesheets',
  variables: {}
});
const predicate = query => {
  return query.queryKey.length === 1 || query.queryKey.length === 2 && typeof query.queryKey[1] === 'object';
};
function setTimesheetsQueryData(queryClient, callback) {
  // Optimistically update to the new value
  return queryClient.setQueriesData({
    queryKey: timesheetsQueryKey,
    predicate
  }, callback);
}
function getTimesheetFromTimesheetsQueryData(queryClient, id) {
  const queryDatas = queryClient.getQueriesData({
    queryKey: timesheetsQueryKey,
    predicate
  });
  for (const queryData of queryDatas) {
    const [, timesheets] = queryData;
    if (!timesheets) continue;
    const timesheet = timesheets.find(timesheet => timesheet.id === id);
    if (timesheet) return timesheet;
  }
}
const invalidateTimesheetsQuery = queryClient => queryClient.invalidateQueries(timesheetsQueryKey);

function getTimesheetQueryKey(id) {
  return queryKeyFn({
    path: '/timesheets/{id}',
    operationId: 'getApiTimesheetsByUuid',
    variables: {
      pathParams: {
        id
      }
    }
  });
}
function invalidateTimesheetQuery(queryClient, id) {
  return queryClient.invalidateQueries(getTimesheetQueryKey(id));
}
function getTimesheetQueryData(queryClient, id) {
  const tsQd = queryClient.getQueryData(getTimesheetQueryKey(id));
  return tsQd ? tsQd : getTimesheetFromTimesheetsQueryData(queryClient, id);
}
const setTimesheetQueryData = (queryClient, id, callback) => {
  setQueryData(queryClient, getTimesheetQueryKey(id), produceCallback(callback));
  // We also update the query data for get timesheets
  setTimesheetsQueryData(queryClient, old => {
    if (!old) return old;
    return produce(old, draft => {
      const timesheet = draft.find(timesheet => timesheet.id === id);
      if (!timesheet) return draft;
      callback(timesheet);
      return draft;
    });
  });
};

function useGetTimesheet(id, options) {
  const queryClient = useQueryClient();
  return useGetApiTimesheetsByUuid({
    pathParams: {
      id: id
    }
  }, _extends({
    staleTime: STALE_TIMES.short,
    cacheTime: CACHE_TIMES.short,
    // Note, `entries` is not included in the initial data because it is not needed for the initial render.
    // @ts-expect-error react-query v5 + OpenAPI does not support `initialData` yet: https://github.com/fabien0102/openapi-codegen/issues/184
    initialData: () => id ? getTimesheetFromTimesheetsQueryData(queryClient, id) : undefined
  }, options, {
    enabled: !!id && (options == null ? void 0 : options.enabled) !== false
  }));
}

function usePostTimesheet(options) {
  const queryClient = useQueryClient();
  // TODO: write reasonable invalidations here. Also, introduce a query cache mechanism that remembers what companyId this timesheet was created on.
  // That way, `GET /api/dimensions` can use the mapping from `timesheetId` to `companyId` to use proper caching mechanisms.
  return usePostApiTimesheets(_extends({}, options, {
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries(timesheetsQueryKey);
      await (options == null || options.onSuccess == null ? void 0 : options.onSuccess(data, variables, context));
    }
  }));
}

function usePostTimesheetDuplicate(options) {
  const queryClient = useQueryClient();
  return usePostApiTimesheetsDuplicate(_extends({}, options, {
    onSuccess: async (data, variables, context) => {
      setTimesheetsQueryData(queryClient, old => {
        if (!old) return old;
        // Add the new timesheet to the cache
        // We want to add to the beginning of the list.
        const ts = _extends({}, data, calculateHours(data.entries));
        return [ts, ...old];
      });
      // Invalidate the cache to force a refetch
      await queryClient.invalidateQueries(timesheetsQueryKey);
      await (options == null || options.onSuccess == null ? void 0 : options.onSuccess(data, variables, context));
    }
  }));
}

function usePostTimesheetRecall(options) {
  const queryClient = useQueryClient();
  // TODO: Implement optimistic updates and cache invalidations
  return usePostApiTimesheetsByUuidRecall(_extends({}, options, {
    onSuccess: async (data, variables, context) => {
      await (options == null || options.onSuccess == null ? void 0 : options.onSuccess(data, variables, context));
      // TODO: Update the cache with the `data`.
      await queryClient.invalidateQueries(timesheetsQueryKey);
    }
  }));
}

function usePostTimesheetSaveAsync(options) {
  const queryClient = useQueryClient();
  return usePostApiTimesheetsByUuidSaveAsync(_extends({}, options, {
    onSuccess: async (data, variables, context) => {
      const {
        id
      } = variables.pathParams;
      setTimesheetQueryData(queryClient, id, draft => {
        draft.saveState = 'SAVING';
        return draft;
      });
      await (options == null || options.onSuccess == null ? void 0 : options.onSuccess(data, variables, context));
    },
    onSettled: async (data, error, variables, context) => {
      const {
        id
      } = variables.pathParams;
      await invalidateTimesheetQuery(queryClient, id);
      await (options == null || options.onSettled == null ? void 0 : options.onSettled(data, error, variables, context));
    }
  }));
}

function usePostTimesheetSaveSettled() {
  const queryClient = useQueryClient();
  return function OnSaveSettled(timesheetId) {
    setTimesheetQueryData(queryClient, timesheetId, draft => {
      draft.saveState = 'NOT_SAVING';
      return draft;
    });
    // This is currently used so that, if you `print` on a fresh timesheet, it will refetch the timesheet to get the recordNo.
    invalidateTimesheetQuery(queryClient, timesheetId);
    invalidateTimesheetRowsQuery(queryClient, timesheetId);
    queryClient.invalidateQueries(getTimesheetEntriesQueryKey(timesheetId));
  };
}

function usePostTimesheetSubmitAsync(options) {
  const queryClient = useQueryClient();
  return usePostApiTimesheetsByUuidSubmitAsync(_extends({}, options, {
    onSuccess: async (data, variables, context) => {
      const {
        id
      } = variables.pathParams;
      setTimesheetQueryData(queryClient, id, draft => {
        draft.submitState = 'SUBMITTING';
        return draft;
      });
      await (options == null || options.onSuccess == null ? void 0 : options.onSuccess(data, variables, context));
    },
    onSettled: async (data, error, variables, context) => {
      const {
        id
      } = variables.pathParams;
      await invalidateTimesheetQuery(queryClient, id);
      await (options == null || options.onSettled == null ? void 0 : options.onSettled(data, error, variables, context));
    }
  }));
}

function usePostTimesheetSubmitSettled() {
  const queryClient = useQueryClient();
  return useCallback(timesheetId => {
    setTimesheetQueryData(queryClient, timesheetId, draft => {
      draft.submitState = 'NOT_SUBMITTING';
      return draft;
    });
    invalidateTimesheetQuery(queryClient, timesheetId);
    invalidateTimesheetRowsQuery(queryClient, timesheetId);
    queryClient.invalidateQueries(getTimesheetEntriesQueryKey(timesheetId));
  }, [queryClient]);
}

function usePutTimesheet(options) {
  const queryClient = useQueryClient();
  return usePutApiTimesheetsByUuid(_extends({}, options, {
    onMutate: async variables => {
      const {
        id
      } = variables.pathParams;
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(getTimesheetQueryKey(id));
      // Snapshot the previous value
      const timesheet = getTimesheetQueryData(queryClient, id);
      // Optimistically update to the new value
      setTimesheetQueryData(queryClient, id, draft => {
        Object.assign(draft, variables.body);
        return draft;
      });
      await (options == null || options.onMutate == null ? void 0 : options.onMutate(variables));
      return {
        timesheet
      };
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (_error, variables, context) => {
      const timesheet = context.timesheet;
      const {
        id
      } = variables.pathParams;
      if (timesheet) {
        setTimesheetQueryData(queryClient, id, () => {
          return timesheet;
        });
      }
      options == null || options.onError == null || options.onError(_error, variables, context);
    },
    onSuccess: async (data, variables, context) => {
      var _variables$body;
      const {
        id
      } = variables.pathParams;
      setTimesheetQueryData(queryClient, id, draft => {
        Object.assign(draft, data);
        return draft;
      });
      if ((_variables$body = variables.body) != null && _variables$body.userId && variables.pathParams.id) {
        const keysToInvalidate = [queryKeyFn({
          path: '/dimensions',
          operationId: 'getApiDimensions',
          variables: {}
        }), queryKeyFn({
          path: '/clients',
          operationId: 'getApiClients',
          variables: {}
        }), queryKeyFn({
          path: '/items',
          operationId: 'getApiItems',
          variables: {}
        }), queryKeyFn({
          path: '/projects',
          operationId: 'getApiProjects',
          variables: {}
        }),
        // @ts-expect-error -- query params
        queryKeyFn({
          path: '/tasks',
          operationId: 'getApiTasks',
          variables: {}
        }), queryKeyFn({
          path: '/time-types',
          operationId: 'getApiTimeTypes',
          variables: {}
        })];
        await Promise.all(keysToInvalidate.map(key => queryClient.invalidateQueries(key)));
      }
      await (options == null || options.onSuccess == null ? void 0 : options.onSuccess(data, variables, context));
    },
    onSettled: async (data, error, variables, context) => {
      const {
        id
      } = variables.pathParams;
      await invalidateTimesheetQuery(queryClient, id);
      await (options == null || options.onSettled == null ? void 0 : options.onSettled(data, error, variables, context));
    }
  }));
}

const useInvalidateTimesheet = () => {
  const client = useQueryClient();
  return useCallback(id => invalidateTimesheetQuery(client, id), [client]);
};

const _excluded = ["startDate", "endDate"],
  _excluded2 = ["startDate", "endDate"];
const transformGetTimesheetResponseItem = _ref => {
  let {
      startDate: startDate_value,
      endDate: endDate_value
    } = _ref,
    timesheet = _objectWithoutPropertiesLoose(_ref, _excluded);
  return _extends({
    startDate: getDateFromString(startDate_value, {
      ISO8601DateExtended: true
    }),
    endDate: getDateFromString(endDate_value, {
      ISO8601DateExtended: true
    })
  }, timesheet);
};
const transformGetApiTimesheetsResponseItem = _ref2 => {
  let {
      startDate: startDate_value,
      endDate: endDate_value
    } = _ref2,
    timesheet = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  return _extends({
    startDate: getStringFromDate(startDate_value),
    endDate: getStringFromDate(endDate_value)
  }, timesheet);
};
const transformGetApiTimesheetsResponse = response => {
  return response.map(timesheet => transformGetApiTimesheetsResponseItem(timesheet));
};
const parseGetApiTimesheetsResponse = response => {
  return response.map(timesheet => transformGetTimesheetResponseItem(timesheet));
};
function useGetTimesheets(variables, options) {
  const result = useGetApiTimesheets(variables, options);
  return _extends({}, result, {
    data: result.data ? parseGetApiTimesheetsResponse(result.data) : undefined
  });
}

// https://tanstack.com/query/v5/docs/react/guides/optimistic-updates#via-the-cache
function useDeleteTimesheets() {
  const queryClient = useQueryClient();
  return usePostApiTimesheetsDelete({
    onMutate: async variables => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(timesheetsQueryKey);
      // Snapshot the previous value
      const queriesData = queryClient.getQueriesData(timesheetsQueryKey);
      // Optimistically update to the new value
      setTimesheetsQueryData(queryClient, old => old == null ? void 0 : old.filter(timesheet => {
        var _variables$body;
        return !((_variables$body = variables.body) != null && (_variables$body = _variables$body.id) != null && _variables$body.includes(`${timesheet.id}`));
      }));
      // Return a context object with the snapshotted value
      return {
        queriesData
      };
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (_error, _variables, context) => {
      for (const [key, value] of context.queriesData) {
        queryClient.setQueriesData(key, value);
      }
    },
    // Always refetch after error or success:
    onSettled: () => {
      return queryClient.invalidateQueries(timesheetsQueryKey);
    }
  });
}

function usePutUserNotificationSettings() {
  const queryClient = useQueryClient();
  const queryKey = queryKeyFn({
    path: '/user-notification-settings',
    operationId: 'getApiUserNotificationSettings',
    variables: {}
  });
  return usePutApiUserNotificationSettings({
    onMutate: async variables => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey
      });
      // Snapshot the previous value
      const previousSettings = queryClient.getQueryData(queryKey);
      // Optimistically update to the new value
      queryClient.setQueryData(queryKey, old => {
        // Edge case where we don't have a previous value to update.
        // Without this `if` statement, we would invalidly assign partial state.
        // This avoid errors like "Cannot read property 'id' of undefined".
        if (!old) return old;
        return immer(old, draft => {
          Object.assign(draft, variables.body);
        });
      });
      // Return a context object with the snapshotted value
      return {
        previousSettings
      };
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(queryKey, context.previousSettings);
    },
    onSuccess: data => {
      queryClient.setQueryData(queryKey, data);
    }
  });
}

function useGetUserSettings(options) {
  return useGetApiUserSettings({}, _extends({}, options, {
    staleTime: STALE_TIMES.long,
    cacheTime: CACHE_TIMES.long
  }));
}
function useGetDefaultShowWeekends() {
  var _data$defaultShowWeek;
  const {
    data
  } = useGetUserSettings();
  return (_data$defaultShowWeek = data == null ? void 0 : data.defaultShowWeekends) != null ? _data$defaultShowWeek : false;
}

const userSettingsQueryKey = queryKeyFn({
  path: '/user-settings',
  operationId: 'getApiUserSettings',
  variables: {}
});

function usePutUserSettings() {
  const queryClient = useQueryClient();
  return usePutApiUserSettings({
    onMutate: async variables => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: userSettingsQueryKey
      });
      // Snapshot the previous value
      const previousSettings = queryClient.getQueryData(userSettingsQueryKey);
      // Optimistically update to the new value
      queryClient.setQueryData(userSettingsQueryKey, old => {
        // Edge case where we don't have a previous value to update.
        // Without this `if` statement, we would invalidly assign partial state.
        // This avoid errors like "Cannot read property 'id' of undefined".
        if (!old) return old;
        return immer(old, draft => {
          Object.assign(draft, variables.body);
        });
      });
      // Return a context object with the snapshotted value
      return {
        previousSettings
      };
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(userSettingsQueryKey, context.previousSettings);
      return queryClient.invalidateQueries(userSettingsQueryKey);
    },
    // Always refetch after error or success:
    onSuccess: data => {
      queryClient.setQueryData(userSettingsQueryKey, data);
    }
  });
}

// My defining `options` outside, we have a stable reference to pass to `useUser`,
// avoiding having to `useCallback` the `select`.
const select$1 = data => data.canListMyTimesheets;
/**
 * @returns Falls back to `true` if the user is not loaded yet to avoid blocking the UI.
 */
const useCanListMyTimesheets = options => {
  var _useGetUser$data;
  return (_useGetUser$data = useGetUser(_extends({}, options, {
    select: select$1
  })).data) != null ? _useGetUser$data : true;
};

// My defining `options` outside, we have a stable reference to pass to `useUser`,
// avoiding having to `useCallback` the `select`.
const options$1 = {
  select: data => data.canViewMyTimesheets
};
/**
 * @returns Falls back to `true` if the user is not loaded yet to avoid blocking the UI.
 */
const useCanViewMyTimesheets = () => {
  var _useGetUser$data;
  return (_useGetUser$data = useGetUser(options$1).data) != null ? _useGetUser$data : true;
};

function useGetUsers(variables, options) {
  var _variables$queryParam;
  return useGetApiUsers(variables, _extends({
    enabled: ((_variables$queryParam = variables.queryParams) == null ? void 0 : _variables$queryParam.companyId) != null,
    cacheTime: 1000 * 60 * 5
  }, options));
}

// My defining `options` outside, we have a stable reference to pass to `useUser`,
// avoiding having to `useCallback` the `select`.
const select = data => data.hasStaffTimesheets;
const useHasStaffTimesheets = options => useGetUser(_extends({}, options, {
  select
})).data;

// avoiding having to `useCallback` the `select`.
const options = {
  select: data => data.role
};
const useUserRole = () => useGetUser(options).data;

function useIsAdmin() {
  const role = useUserRole();
  const hasStaffTimesheets = useHasStaffTimesheets();
  return role === 'admin' || hasStaffTimesheets || false;
}

function usePostUserActivateMfa(options) {
  return usePostApiUsersActivate2Fa(_extends({}, options));
}

function usePostUserChangePassword(options) {
  return usePostApiUsersChangePassword(options);
}

function usePostUserDeactivateMfa(options) {
  const queryClient = useQueryClient();
  return usePostApiUsersDeactivate2Fa({
    onSuccess: async (response, variables, context) => {
      setUserQueryData(queryClient, old => {
        if (old) {
          old.twoFactorEnabled = false;
        }
        return old;
      });
      invalidateUserQuery(queryClient);
      await (options == null || options.onSuccess == null ? void 0 : options.onSuccess(response, variables, context));
    }
  });
}

function usePostUserVerifyEmail(options) {
  const queryClient = useQueryClient();
  return usePostApiPubVerify({
    onSuccess: (response, variables, context) => {
      options == null || options.onSuccess == null || options.onSuccess(response, variables, context);
      queryClient.setQueryData(getUserQueryKey, response);
    }
  });
}

function usePostUserVerifyMfa(options) {
  return usePostApiUsersVerify2Fa(options);
}

const teamQueryKey = queryKeyFn({
  path: '/team',
  operationId: 'getApiTeam',
  variables: {}
});

function setTeamMemberQueryData(queryClient, userId, updater) {
  queryClient.setQueryData(teamQueryKey, oldData => produce(oldData, draft => {
    if (!draft) return;
    const user = draft.find(u => u.id === userId);
    if (user) {
      updater(user);
    }
    return draft;
  }));
}

function usePutUser(options) {
  const queryClient = useQueryClient();
  return usePutApiUsersByUuid(_extends({}, options, {
    onMutate: async variables => {
      const {
        userId
      } = variables.pathParams;
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: getUserQueryKey
      });
      // Snapshot the previous value
      const userQueryData = getUserQueryData(queryClient);
      // Update my user data.
      if (userQueryData && userQueryData.id === userId) {
        setUserQueryData(queryClient, draft => {
          if (!draft) return;
          Object.assign(draft, variables.body);
          return draft;
        });
      }
      const teamQueryData = queryClient.getQueryData(teamQueryKey);
      setTeamMemberQueryData(queryClient, userId, draft => {
        Object.assign(draft, variables.body);
      });
      // Return a context object with the snapshotted value
      return {
        userQueryData,
        teamQueryData
      };
    },
    onError: (error, variables, context) => {
      const {
        userId
      } = variables.pathParams;
      const ctx = context;
      if (ctx.userQueryData && ctx.userQueryData.id === userId) {
        setUserQueryData(queryClient, () => context.userQueryData);
      }
      options == null || options.onError == null || options.onError(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      const {
        userId
      } = variables.pathParams;
      const ctx = context;
      if (ctx.userQueryData && ctx.userQueryData.id === userId) {
        setUserQueryData(queryClient, () => data);
      }
      setTeamMemberQueryData(queryClient, userId, draft => {
        Object.assign(draft, data);
      });
      options == null || options.onSuccess == null || options.onSuccess(data, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      queryClient.invalidateQueries(teamQueryKey);
      options == null || options.onSettled == null || options.onSettled(data, error, variables, context);
    }
  }));
}

const usePostApiMultiPartAttachment = options => {
  const {
    fetcherOptions
  } = useSitContext();
  return reactQuery.useMutation(async variables => {
    const formData = new FormData();
    for (const file of variables.body.files) {
      formData.append('files', file);
    }
    formData.append('fileNamesToDelete', variables.body.fileNamesToDelete);
    return await fetchPostApiTimesheetsAttachmentByUuid(_extends({}, fetcherOptions, variables, {
      // @ts-expect-error -- overriding the body type to be FormData
      body: formData,
      headers: _extends({}, fetcherOptions.headers, {
        'Content-Type': 'multipart/form-data'
      })
    }));
  }, options);
};

export { CACHE_TIMES, ErrorClassification, PERSIST_META, RequestError, STALE_TIMES, addAfterRetryErrorInterceptor, addBeforeRetryErrorInterceptor, axiosInstance, buildHeaders, calculateHours, dataStatusQueryKey, dimensionPageSize, fetchDeleteApiAccount, fetchDeleteApiAdminCompanyTreeSettings, fetchDeleteApiCluster, fetchDeleteApiClusterNode, fetchDeleteApiTimesheetsAttachmentByUuid, fetchDeleteApiTimesheetsByUuidRows, fetchGetApiAccounts, fetchGetApiAdminCompanies, fetchGetApiAdminCompanyByCompanyTreeSettingsId, fetchGetApiAdminCompanyUsers, fetchGetApiAdminIntacctCompanyInfo, fetchGetApiAdminIsAuthenticated, fetchGetApiAdminJobStatus, fetchGetApiAdminScheduledJobs, fetchGetApiAdminSyncStatus, fetchGetApiAdminTriggersPackages, fetchGetApiAdminUserById, fetchGetApiApprovalsCheck, fetchGetApiApprovalsEntries, fetchGetApiApprovalsTimesheets, fetchGetApiClients, fetchGetApiClustersForTimesheet, fetchGetApiCompany, fetchGetApiDataStatus, fetchGetApiDimensions, fetchGetApiEmployees, fetchGetApiIntacctIntegrationProvisionJobStatus, fetchGetApiIntacctRunSync, fetchGetApiIntacctRunTimesheetsSync, fetchGetApiIntacctSyncStatus, fetchGetApiIntacctSyncTimesheetsStatus, fetchGetApiItems, fetchGetApiNotifications, fetchGetApiProjects, fetchGetApiPubInvitesInviteToken, fetchGetApiServiceHealth, fetchGetApiTasks, fetchGetApiTeam, fetchGetApiTimeAssistantDownloadLinks, fetchGetApiTimeTypes, fetchGetApiTimesheetAttachmentFile, fetchGetApiTimesheetTimeEntries, fetchGetApiTimesheets, fetchGetApiTimesheetsAttachmentByUuid, fetchGetApiTimesheetsByExternalUniqueId, fetchGetApiTimesheetsByUuid, fetchGetApiTimesheetsByUuidEntries, fetchGetApiTimesheetsByUuidRows, fetchGetApiTimesheetsEntryApproveDeclineAllowed, fetchGetApiTimesheetsEntryById, fetchGetApiUser, fetchGetApiUserNotificationSettings, fetchGetApiUserSettings, fetchGetApiUsers, fetchGetApiValidateResetToken, fetchPatchApiAdminCompany, fetchPatchApiAdminCompanyEntity, fetchPostApiAdminProvision, fetchPostApiAdminScheduleJob, fetchPostApiAdminSyncTree, fetchPostApiAdminSyncTriggersPackages, fetchPostApiAdminTriggersPackages, fetchPostApiAdminUserSetPassword, fetchPostApiAuthIntacctAccessToken, fetchPostApiAuthLogin, fetchPostApiAuthLoginBiometrics, fetchPostApiAuthMfaFallback, fetchPostApiClusterSplit, fetchPostApiForgotPassword, fetchPostApiIntacctIntegrationAccessToken, fetchPostApiIntacctIntegrationHandleChange, fetchPostApiIntacctIntegrationProvision, fetchPostApiIntacctIntegrationUserRestToken, fetchPostApiIntacctRunSync, fetchPostApiIntacctRunTimesheetsSync, fetchPostApiNotificationsReadAll, fetchPostApiPubEmployeeSignup, fetchPostApiPubToken, fetchPostApiPubVerify, fetchPostApiResetPassword, fetchPostApiTeamBulkInvite, fetchPostApiTeamByUuidInvite, fetchPostApiTimesheets, fetchPostApiTimesheetsAttachmentByUuid, fetchPostApiTimesheetsByUuidEntries, fetchPostApiTimesheetsByUuidRecall, fetchPostApiTimesheetsByUuidRows, fetchPostApiTimesheetsByUuidSaveAsync, fetchPostApiTimesheetsByUuidSubmitAsync, fetchPostApiTimesheetsDelete, fetchPostApiTimesheetsDuplicate, fetchPostApiUserConsents, fetchPostApiUserConsentsRevoke, fetchPostApiUsersActivate2Fa, fetchPostApiUsersChangePassword, fetchPostApiUsersDeactivate2Fa, fetchPostApiUsersLogout, fetchPostApiUsersVerify2Fa, fetchPostApiUsersVerifyPassword, fetchPutApiAccount, fetchPutApiApprovalsApproveAsync, fetchPutApiApprovalsDeclineAsync, fetchPutApiCalendarCalendarsId, fetchPutApiCluster, fetchPutApiClusterNode, fetchPutApiClusterNodeOverlaps, fetchPutApiCompaniesByUuid, fetchPutApiTimesheetsByUuid, fetchPutApiTimesheetsByUuidEntriesByEntryId, fetchPutApiTimesheetsByUuidRows, fetchPutApiTimesheetsByUuidRowsBillable, fetchPutApiTimesheetsByUuidRowsDimensionValues, fetchPutApiUserActions, fetchPutApiUserNotificationSettings, fetchPutApiUserSettings, fetchPutApiUsersByUuid, getAdminTriggersPackagesQueryKey, getAuthorizationHeader, getEmployeesQueryData, getHeaders, getRefreshToken, getResponseErrorMsg, getTimesheetFromTimesheetsQueryData, getTimesheetQueryData, getTimesheetQueryKey, getToken, getUserQueryData, handleCommonStringErrors, handleResponseErrors, initializeAuthCallbacks, invalidateAdminCompanyTreeQueryData, invalidateDataStatusQuery, invalidateTimesheetQuery, invalidateTimesheetsQuery, invalidateUserQuery, isResponse, logoutUser, onLogout, parseGetApiTimesheetsResponse, parseResponseError, queryKeyFn, setBaseUrl, setQueriesData, setQueryData, setTimesheetQueryData, setTimesheetsQueryData, setToken, setUserQueryData, sitFetch, timesheetsQueryKey, transformGetApiTimesheetsResponse, updateAdminCompanyTreeQueryData, useCanListMyTimesheets, useCanViewMyTimesheets, useDeleteApiAccount, useDeleteApiAccountOptimistic, useDeleteApiAdminCompanyTreeSettings, useDeleteApiCluster, useDeleteApiClusterNode, useDeleteApiTimesheetsAttachmentByUuid, useDeleteApiTimesheetsByUuidRows, useDeleteClusterNode, useDeleteTimesheetRow, useDeleteTimesheets, useGetAdminIsAuthenticated, useGetApiAccounts, useGetApiAdminCompanies, useGetApiAdminCompanyByCompanyTreeSettingsId, useGetApiAdminCompanyUsers, useGetApiAdminIntacctCompanyInfo, useGetApiAdminIsAuthenticated, useGetApiAdminJobStatus, useGetApiAdminScheduledJobs, useGetApiAdminSyncStatus, useGetApiAdminTriggersPackages, useGetApiAdminUserById, useGetApiApprovalsCheck, useGetApiApprovalsEntries, useGetApiApprovalsTimesheets, useGetApiClients, useGetApiClientsInfinite, useGetApiClustersForTimesheet, useGetApiCompany, useGetApiDataStatus, useGetApiDimensions, useGetApiEmployees, useGetApiIntacctIntegrationProvisionJobStatus, useGetApiIntacctRunSync, useGetApiIntacctRunTimesheetsSync, useGetApiIntacctSyncStatus, useGetApiIntacctSyncTimesheetsStatus, useGetApiItems, useGetApiItemsInfinite, useGetApiNotifications, useGetApiProjects, useGetApiProjectsInfinite, useGetApiPubInvitesInviteToken, useGetApiServiceHealth, useGetApiTasks, useGetApiTasksInfinite, useGetApiTeam, useGetApiTimeAssistantDownloadLinks, useGetApiTimeTypes, useGetApiTimeTypesInfinite, useGetApiTimesheetAttachmentFile, useGetApiTimesheetTimeEntries, useGetApiTimesheets, useGetApiTimesheetsAttachmentByUuid, useGetApiTimesheetsByExternalUniqueId, useGetApiTimesheetsByUuid, useGetApiTimesheetsByUuidEntries, useGetApiTimesheetsByUuidRows, useGetApiTimesheetsEntryApproveDeclineAllowed, useGetApiTimesheetsEntryById, useGetApiUser, useGetApiUserNotificationSettings, useGetApiUserSettings, useGetApiUsers, useGetApiValidateResetToken, useGetApprovalEntries, useGetApprovalTimesheets, useGetClients, useGetClustersByTimesheet, useGetCompany, useGetCompanyById, useGetDefaultShowWeekends, useGetDimensions, useGetItems, useGetNotifications, useGetProjects, useGetTasks, useGetTeam, useGetTimeEntries, useGetTimeTypes, useGetTimesheet, useGetTimesheetEntries, useGetTimesheetEntry, useGetTimesheetEntryFromEntries, useGetTimesheetRows, useGetTimesheets, useGetUser, useGetUserSettings, useGetUsers, useHasStaffTimesheets, useIntacctCompanyId, useInvalidateDataStatus, useInvalidateGetApprovalEntries, useInvalidateGetApprovalTimesheets, useInvalidateGetTimesheetRows, useInvalidateTimesheet, useIsAdmin, usePatchAdminCompany, usePatchAdminCompanyEntity, usePatchApiAdminCompany, usePatchApiAdminCompanyEntity, usePostAdminSyncTriggersPackages, usePostAdminTriggersPackages, usePostApiAdminProvision, usePostApiAdminScheduleJob, usePostApiAdminSyncTree, usePostApiAdminSyncTriggersPackages, usePostApiAdminTriggersPackages, usePostApiAdminUserSetPassword, usePostApiAuthIntacctAccessToken, usePostApiAuthLogin, usePostApiAuthLoginBiometrics, usePostApiAuthMfaFallback, usePostApiClusterSplit, usePostApiForgotPassword, usePostApiIntacctIntegrationAccessToken, usePostApiIntacctIntegrationHandleChange, usePostApiIntacctIntegrationProvision, usePostApiIntacctIntegrationUserRestToken, usePostApiIntacctRunSync, usePostApiIntacctRunTimesheetsSync, usePostApiMultiPartAttachment, usePostApiNotificationsReadAll, usePostApiPubEmployeeSignup, usePostApiPubToken, usePostApiPubVerify, usePostApiResetPassword, usePostApiTeamBulkInvite, usePostApiTeamByUuidInvite, usePostApiTimesheets, usePostApiTimesheetsAttachmentByUuid, usePostApiTimesheetsByUuidEntries, usePostApiTimesheetsByUuidRecall, usePostApiTimesheetsByUuidRows, usePostApiTimesheetsByUuidSaveAsync, usePostApiTimesheetsByUuidSubmitAsync, usePostApiTimesheetsDelete, usePostApiTimesheetsDuplicate, usePostApiUserConsents, usePostApiUserConsentsRevoke, usePostApiUsersActivate2Fa, usePostApiUsersChangePassword, usePostApiUsersDeactivate2Fa, usePostApiUsersLogout, usePostApiUsersVerify2Fa, usePostApiUsersVerifyPassword, usePostClusterSplit, usePostEmployeeSignup, usePostLogin, usePostNotificationsReadAll, usePostTimesheet, usePostTimesheetDuplicate, usePostTimesheetEntries, usePostTimesheetRecall, usePostTimesheetRow, usePostTimesheetSaveAsync, usePostTimesheetSaveSettled, usePostTimesheetSubmitAsync, usePostTimesheetSubmitSettled, usePostUserActivateMfa, usePostUserChangePassword, usePostUserDeactivateMfa, usePostUserVerifyEmail, usePostUserVerifyMfa, usePutApiAccount, usePutApiApprovalsApproveAsync, usePutApiApprovalsDeclineAsync, usePutApiCalendarCalendarsId, usePutApiCalendarOptimistic, usePutApiCluster, usePutApiClusterNode, usePutApiClusterNodeOverlaps, usePutApiCompaniesByUuid, usePutApiTimesheetsByUuid, usePutApiTimesheetsByUuidEntriesByEntryId, usePutApiTimesheetsByUuidRows, usePutApiTimesheetsByUuidRowsBillable, usePutApiTimesheetsByUuidRowsDimensionValues, usePutApiUserActions, usePutApiUserNotificationSettings, usePutApiUserSettings, usePutApiUsersByUuid, usePutCluster, usePutClusterNode, usePutCompany, usePutTimesheet, usePutTimesheetEntry, usePutTimesheetRow, usePutTimesheetRowBillable, usePutTimesheetRowDimensionValues, usePutUser, usePutUserNotificationSettings, usePutUserSettings, useSitContext, useUserRole, useValidateInvite };
//# sourceMappingURL=index.modern.js.map
